import { normalizeTeam, sumTeamScores, teamForSlotIndex } from './teams.js';

import {
  GEM_MODE_POWERUP_WEIGHTS,
  BASIC_BALANCE,
  matchTarget
} from './game-balance.js';

const FIELD_HEIGHT = BASIC_BALANCE.fieldHeight;
const FIELD_WIDTH = BASIC_BALANCE.fieldWidth;
const FIELD_RES = 4;
const OCC_STEP = 1 / FIELD_RES;
// Coarse summary of the occupancy grid: per 16x16-cell block (4x4 px), the
// number of occupied cells. Lets disc probes skip the fine cell scan entirely
// when every covering block is empty, which is the common case in open space.
// The grid's own dimensions are per-engine (this.occW/occH/occBW/occBH) because
// the field is sized from the player count; only the block geometry is fixed.
const OCC_BLOCK_SHIFT = 4;

function clampFieldDimension(value, fallback) {
  const parsed = Math.round(Number(value));
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return parsed;
}

const TICK_MS = BASIC_BALANCE.tickMs;
const LEGACY_DURATION_TICK_MS = 40;
const BIKE_SPEED = BASIC_BALANCE.bikeSpeed;
const TURN_RADIUS = BASIC_BALANCE.turnRadius;
const STEER_RAMP_MS = 0;
const TRAIL_SIZE = BASIC_BALANCE.trailSize;
const CURVE_COLLISION_SIZE = BASIC_BALANCE.collisionSize;
const CURVE_COLLISION_RADIUS_FACTOR = CURVE_COLLISION_SIZE / TRAIL_SIZE;
const TRAIL_DRAW_SUBSTEPS = 2;
const POWERUP_RADIUS = BASIC_BALANCE.powerupRadius;
const POWERUP_GRAB_RADIUS = BASIC_BALANCE.powerupRadius;
const POWERUP_MAX_ACTIVE = 8;
const POWERUP_PLAYER_SAFE_RADIUS = 38;
const POWERUP_WALL_MARGIN = POWERUP_RADIUS;
const POWERUP_SPAWN_WEIGHT_TOTAL = Object.values(GEM_MODE_POWERUP_WEIGHTS)
  .reduce((sum, weight) => sum + weight, 0);
const POWERUP_FREQUENCY_PER_SECOND = BASIC_BALANCE.powerupSpawnFrequencyPerSecond;
const POWERUP_SPAWN_CHANCE_PER_TICK = clamp(POWERUP_FREQUENCY_PER_SECOND * (TICK_MS / 1000), 0, 1);
const POWERUP_FIRST_SPAWN_TICKS = Math.max(1, Math.round(0.4 * 1000 / TICK_MS));
const POWERUP_SPAWN_RETRY_TICKS = Math.max(1, Math.round(0.25 * 1000 / TICK_MS));
const HEAD_SPAWN_TIMER_MS = 200;
const HEAD_SPAWN_SCALE_DELAY_MS = 200;
const HEAD_SPAWN_SCALE_MS = 300;
const HEAD_SPAWN_STAGGER_MS = 100;
const HEAD_SPAWN_START_SCALE_BASE = 3;
const HEAD_SPAWN_START_SCALE_STEP = 0.1;
const HEAD_SPAWN_MARKER_MS = 3000;
const FLY_GRACE_SECONDS = 2.44;
const FLY_GRACE_MS = FLY_GRACE_SECONDS * 1000;
const FLY_GRACE_TICKS = Math.ceil(FLY_GRACE_MS / TICK_MS);

const GEM_ROUND_DURATION_MS = 2 * 60 * 1000;
const RESPAWN_DELAY_MS = 1500;
const RESPAWN_DELAY_TICKS = durationTicks(RESPAWN_DELAY_MS);
const RESPAWN_TRAIL_GAP_DISTANCE = BIKE_SPEED * FLY_GRACE_SECONDS;
const RESPAWN_SAFE_RADIUS = 48;
const RESPAWN_MARKER_MS = FLY_GRACE_MS;

const HOLE_START_DELAY_TICKS = Math.max(1, Math.ceil(0.4 * 1000 / TICK_MS));
const HOLE_SIZE = BASIC_BALANCE.holeSize;
const HOLE_FREQUENCY_PER_SECOND = BASIC_BALANCE.holeFrequencyPerSecond;
const GAP_START_CHANCE_PER_TICK = clamp(HOLE_FREQUENCY_PER_SECOND * (TICK_MS / 1000), 0, 1);

const COLLISION_SELF_GRACE_TICKS = legacyDurationTicks(6);
const TRAIL_OCC_RADIUS_FACTOR = CURVE_COLLISION_RADIUS_FACTOR;
const COLLISION_OCC_RADIUS_INSET = (TRAIL_SIZE / 2) * (1 - CURVE_COLLISION_RADIUS_FACTOR);
const HEAD_SWEEP_COLLISION_FACTOR = CURVE_COLLISION_RADIUS_FACTOR;

const INTERROUND_DELAY_MS = 6000;

const POINTS_PER_SURVIVE = BASIC_BALANCE.pointsPerSurvive;
const POINTS_PER_KILL = BASIC_BALANCE.pointsPerKill;
const ERASE_DEAD_LINES = BASIC_BALANCE.eraseDeadLines;
const ALWAYS_ACTIVE_POWERUPS = BASIC_BALANCE.alwaysActivePowerups;
const DEATH_DEBUG_LIMIT = 36;
const SPAWN_GRACE_TICKS = legacyDurationTicks(22);
const ROUND_END_MIN_TICKS = legacyDurationTicks(20);

const PU_TYPES = {
  SPEED: 'SPEED',
  CLEAR: 'CLEAR',
  GEM: 'GEM'
};

const PU_DEF = {
  SPEED: {
    iconMode: 'image',
    iconSrc: 'assets/icons/green_speed.svg',
    colors: ['#86ca36', '#86ca36', '#6bb511', '#4b810a']
  },
  CLEAR: {
    iconMode: 'image',
    iconSrc: 'assets/icons/eraser.svg',
    colors: ['#469cf4', '#469cf4', '#2178d2', '#1264b7']
  },
  GEM: {
    iconMode: 'image',
    iconSrc: 'assets/icons/gem.svg',
    colors: ['#ff8a1f', '#ff8a1f', '#e66b12', '#a94708']
  }
};

const GEM_VARIANTS = [
  {
    name: 'orange',
    value: 6,
    weight: 72,
    colors: ['#ff8a1f', '#ff8a1f', '#e66b12', '#a94708']
  },
  {
    name: 'red',
    value: 12,
    weight: 22,
    colors: ['#e34f38', '#e34f38', '#b82824', '#7d1718']
  },
  {
    name: 'purple',
    value: 18,
    weight: 6,
    colors: ['#a855f7', '#a855f7', '#7c2fd6', '#51208b']
  }
];
const GEM_VARIANT_WEIGHT_TOTAL = GEM_VARIANTS.reduce((sum, variant) => sum + variant.weight, 0);

const SPEED_EFFECT_TICKS = Math.max(1, Math.round(
  (BASIC_BALANCE.speedPowerupDurationSeconds * 1000) / TICK_MS
));
const SPEED_STACK_SPEED_BONUS = BASIC_BALANCE.speedPowerupSpeedMultiplier - 1;
const SPEED_STACK_TURN_RADIUS_MUL = BASIC_BALANCE.speedPowerupTurnRadiusMultiplier;
// Uncapped by default, for parity with CF2/CurveCrash. Anything non-finite or
// non-positive means "no limit"; a positive integer is honoured as a real cap.
const SPEED_STACK_MAX = (
  Number.isFinite(Number(BASIC_BALANCE.speedPowerupMaxStacks))
  && Number(BASIC_BALANCE.speedPowerupMaxStacks) > 0
)
  ? Math.floor(Number(BASIC_BALANCE.speedPowerupMaxStacks))
  : Infinity;
const TICK_FRAME_EPSILON_MS = 0.0001;

// One color per hue family (plus white) so no two curves are ever hard to
// tell apart mid-match. The server randomly chooses among these solids for
// automatic assignments; array order only keeps ids stable on the wire.
const COLORS = [
  '#ffe135', // yellow
  '#ff9224', // orange
  '#ff4053', // red
  '#ff6ec7', // pink
  '#a95cff', // purple
  '#4769ff', // blue
  '#22dfdf', // cyan
  '#3ee06b', // green
  '#f5f6ff'  // white
];

// A disconnected player's curve and head desaturate to this grey for the
// duration of the mid-match reconnect grace window (Curve Fever style); their
// real color returns the instant they resume. Kept here so both the server's
// trail painting and the client's head rendering agree on the exact shade.
const DISCONNECTED_COLOR = '#6b7280';

// Display names for COLORS, index-aligned. Used by the lobby color picker
// and the chat notice when a player switches colors.
const COLOR_NAMES = [
  'Lightning Lemon', 'Tangerine Turbo', 'Crimson Dash', 'Bubblegum Drift',
  'Neon Orchid', 'Royal Bolt', 'Cyber Cyan', 'Venom Green', 'Ghost Light'
];

// Mesh colors: gradients fixed to the ARENA, not to the snake. A mesh trail's
// hue depends on where on the field it was drawn, so crossing the arena sweeps
// the whole gradient and a trail laid in the same place always looks the same.
// Painted as one linear gradient along the field diagonal.
//
// `base` is the flat color everything gradient-unaware falls back to: roster
// chips, chat names, the scoreboard, and any client too old to know about
// mesh. Picked as the middle stop so the fallback still reads as "that player".
//
// Ids continue after COLORS so no existing colorId ever moves. Auto-assignment
// only ever hands out solids (see assignRosterColors) -- a mesh is opt-in.
const MESH_COLORS = [
  { name: 'Solar Flare', base: '#ff7a1a', stops: ['#ffe135', '#ff7a1a', '#ff4053'] },
  { name: 'Vapor Drift', base: '#c86bff', stops: ['#ff6ec7', '#c86bff', '#4769ff'] },
  { name: 'Reef Current', base: '#22dfdf', stops: ['#3ee06b', '#22dfdf', '#4769ff'] },
  { name: 'Ember Bloom', base: '#ff4d8d', stops: ['#ff4053', '#ff4d8d', '#a95cff'] },
  { name: 'Aurora Wire', base: '#5ce6b0', stops: ['#22dfdf', '#5ce6b0', '#ffe135'] },
  { name: 'Deep Signal', base: '#7a8dff', stops: ['#4769ff', '#7a8dff', '#f5f6ff'] }
];

const MESH_COLOR_OFFSET = COLORS.length;
const PALETTE_SIZE = COLORS.length + MESH_COLORS.length;
// Player style, not a viewer preference: the server validates this floor and
// every client renders the same alpha. Keeping trails at least this visible
// preserves them as readable collision obstacles.
const TRAIL_OPACITY_MIN_PERCENT = 70;

function isMeshColorId(colorId) {
  return Number.isInteger(colorId)
    && colorId >= MESH_COLOR_OFFSET
    && colorId < PALETTE_SIZE;
}

function meshForColorId(colorId) {
  return isMeshColorId(colorId) ? MESH_COLORS[colorId - MESH_COLOR_OFFSET] : null;
}

// The flat color for any palette id, mesh included.
function paletteColorForId(colorId) {
  const mesh = meshForColorId(colorId);
  if (mesh) return mesh.base;
  return COLORS[colorId] || COLORS[0];
}

function paletteNameForId(colorId) {
  const mesh = meshForColorId(colorId);
  if (mesh) return mesh.name;
  return COLOR_NAMES[colorId] || paletteColorForId(colorId);
}

// Gradient stops for a mesh id, or null for a solid. Copied so callers cannot
// mutate the palette.
function meshStopsForColorId(colorId) {
  const mesh = meshForColorId(colorId);
  return mesh ? mesh.stops.slice() : null;
}

function createSeededRandom(seed = Date.now()) {
  let state = (Number(seed) >>> 0) || 0x6d2b79f5;
  const next = () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  next.getState = () => state >>> 0;
  next.setState = (nextState) => {
    state = (Number(nextState) >>> 0) || 0x6d2b79f5;
  };
  return next;
}

function createGameId(now = Date.now()) {
  const base = Number.isFinite(Number(now)) ? Number(now) : Date.now();
  const stamp = Math.max(0, Math.floor(base)).toString(36);
  const rand = Math.floor(Math.random() * 0x7fffffff).toString(36);
  return `${stamp}-${rand}`;
}

function durationTicks(durationMs) {
  return Math.max(1, Math.round(durationMs / TICK_MS));
}

function legacyDurationTicks(ticks) {
  return durationTicks(ticks * LEGACY_DURATION_TICK_MS);
}

function trailOpOwnerId(owner) {
  const value = Number(owner);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.max(0, Math.floor(value) - 1);
}

// Wire precision. Positions are quantised to 1/100 px and angles to 1e-4 rad:
// both are far below what a canvas can resolve, but a full double serialises as
// ~18 characters against ~6. These are serialisation-only -- the engine's own
// state, the occupancy grid and every collision test keep full precision.
const WIRE_PX_DECIMALS = 100;
const WIRE_RAD_DECIMALS = 10000;

function wirePx(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * WIRE_PX_DECIMALS) / WIRE_PX_DECIMALS : n;
}

function wireRad(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * WIRE_RAD_DECIMALS) / WIRE_RAD_DECIMALS : n;
}

function quantiseTrailOp(op) {
  if (!op || (op.k !== 's' && op.k !== 'p')) return op;
  const out = { ...op };
  if (op.k === 's') {
    out.x1 = wirePx(op.x1);
    out.y1 = wirePx(op.y1);
    out.x2 = wirePx(op.x2);
    out.y2 = wirePx(op.y2);
  } else {
    out.x = wirePx(op.x);
    out.y = wirePx(op.y);
  }
  out.r = wirePx(op.r);
  return out;
}

function clamp(v, a, b) {
  return Math.max(a, Math.min(b, v));
}

function lerpAngle(a, b, t) {
  const tt = clamp(t, 0, 1);
  const delta = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + delta * tt;
}

function pointSegmentDistSq(px, py, ax, ay, bx, by) {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq <= 1e-12) {
    const dx = px - ax;
    const dy = py - ay;
    return dx * dx + dy * dy;
  }
  const t = clamp((apx * abx + apy * aby) / abLenSq, 0, 1);
  const qx = ax + abx * t;
  const qy = ay + aby * t;
  const dx = px - qx;
  const dy = py - qy;
  return dx * dx + dy * dy;
}

function orient(ax, ay, bx, by, cx, cy) {
  return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
}

function onSegment(ax, ay, bx, by, px, py, eps = 1e-9) {
  return (
    px >= Math.min(ax, bx) - eps
    && px <= Math.max(ax, bx) + eps
    && py >= Math.min(ay, by) - eps
    && py <= Math.max(ay, by) + eps
  );
}

function segmentsIntersect(ax, ay, bx, by, cx, cy, dx, dy) {
  const o1 = orient(ax, ay, bx, by, cx, cy);
  const o2 = orient(ax, ay, bx, by, dx, dy);
  const o3 = orient(cx, cy, dx, dy, ax, ay);
  const o4 = orient(cx, cy, dx, dy, bx, by);
  const eps = 1e-9;

  if (Math.abs(o1) <= eps && onSegment(ax, ay, bx, by, cx, cy, eps)) return true;
  if (Math.abs(o2) <= eps && onSegment(ax, ay, bx, by, dx, dy, eps)) return true;
  if (Math.abs(o3) <= eps && onSegment(cx, cy, dx, dy, ax, ay, eps)) return true;
  if (Math.abs(o4) <= eps && onSegment(cx, cy, dx, dy, bx, by, eps)) return true;

  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
}

function segmentSegmentDistSq(ax, ay, bx, by, cx, cy, dx, dy) {
  if (segmentsIntersect(ax, ay, bx, by, cx, cy, dx, dy)) return 0;
  let best = pointSegmentDistSq(ax, ay, cx, cy, dx, dy);
  best = Math.min(best, pointSegmentDistSq(bx, by, cx, cy, dx, dy));
  best = Math.min(best, pointSegmentDistSq(cx, cy, ax, ay, bx, by));
  best = Math.min(best, pointSegmentDistSq(dx, dy, ax, ay, bx, by));
  return best;
}

class CurveGameEngine {
  constructor(options = {}) {
    const parsedHumanCount = Number.parseInt(options.humanCount, 10);
    const parsedBotCount = Number.parseInt(options.botCount, 10);
    this.humanCount = Number.isFinite(parsedHumanCount) ? Math.max(0, parsedHumanCount) : 1;
    this.botCount = Number.isFinite(parsedBotCount) ? Math.max(0, parsedBotCount) : 1;
    this.autoRoundEndSinglePlayer = options.autoRoundEndSinglePlayer !== false;
    this.seed = Number.isFinite(Number(options.seed)) ? (Number(options.seed) >>> 0) : (Date.now() >>> 0);
    this.random = createSeededRandom(this.seed);
    // Optional per-player color overrides indexed by player id; gaps fall
    // back to the default palette rotation.
    // Gradient stops per player, index-aligned with playerColors; null for a
    // solid. Carried, never interpreted -- the engine only ever needs the flat
    // color, and only the renderer knows what to do with a gradient.
    this.playerMeshes = Array.isArray(options.playerMeshes)
      ? options.playerMeshes.map((stops) => (
        Array.isArray(stops) && stops.length > 1
          ? stops.filter((stop) => typeof stop === 'string' && stop).slice(0, 8)
          : null
      ))
      : [];
    this.playerColors = Array.isArray(options.playerColors)
      ? options.playerColors.map((color) => (typeof color === 'string' && color ? color : null))
      : [];
    // Which side each player is on, index-aligned with playerColors. Empty
    // outside team mode, and the engine treats "no team" as the normal case
    // everywhere rather than branching on a mode flag.
    this.playerTeams = Array.isArray(options.playerTeams)
      ? options.playerTeams.map((team) => normalizeTeam(team))
      : [];
    // Optional authoritative base radius per engine player id. Missing entries
    // inherit the shared match value; effects still multiply the resolved base.
    this.playerTurnRadii = Array.isArray(options.playerTurnRadii)
      ? options.playerTurnRadii.map((radius) => (
        Number.isFinite(Number(radius)) && Number(radius) > 0 ? Number(radius) : null
      ))
      : [];
    this.teamMode = this.playerTeams.some((team) => team !== null);

    // Field size is per-match now: CF2's scaling rule sizes it from the player
    // count (see scaledFieldSize), and everything derived from it -- the
    // occupancy grid and its coarse block summary -- has to be sized to match.
    // The module constants above are the FULL-size default, which is what a
    // room that is not scaling still plays on.
    this.fieldWidth = clampFieldDimension(options.fieldWidth, FIELD_WIDTH);
    this.fieldHeight = clampFieldDimension(options.fieldHeight, FIELD_HEIGHT);
    this.tickMs = TICK_MS;

    this.occW = this.fieldWidth * FIELD_RES;
    this.occH = this.fieldHeight * FIELD_RES;
    this.occBW = Math.ceil(this.occW / (1 << OCC_BLOCK_SHIFT));
    this.occBH = Math.ceil(this.occH / (1 << OCC_BLOCK_SHIFT));

    this.occ = new Uint8Array(this.occW * this.occH);
    this.occOwner = new Uint8Array(this.occW * this.occH);
    this.occTick = new Uint16Array(this.occW * this.occH);
    this.occBlocks = new Uint16Array(this.occBW * this.occBH);
    // Painting records the small set of coarse occupancy blocks touched by
    // each player. A crash can scan those blocks instead of the entire
    // 2880x2880 occupancy grid on the server's 20 ms tick thread.
    this.occBlocksByOwner = new Map();

    this.playerInputs = new Map();
    for (let i = 0; i < this.humanCount; i++) {
      this.playerInputs.set(i, { left: false, right: false });
    }
    // Engine player ids whose owner is disconnected (mid-match grace window).
    // Their trail is painted grey and the client greys their head; it survives
    // round transitions within this engine and is restored on resume. Lives on
    // the engine (like playerInputs) rather than the per-round player object.
    this.disconnectedPlayers = new Set();
    this.lastTickMs = Date.now();

    this.players = [];
    this.scores = [];
    this.roundScores = [];
    this.matchActivity = [];
    this.matchTickCount = 0;

    this.tickCounter = 0;
    this.running = false;
    this.waitingNext = false;
    this.gameOver = false;
    this.manualPaused = false;

    this.roundWinnerId = -1;
    this.matchWinnerId = -1;
    // True when the match ends with the top score shared by 2+ players; the
    // winner id still resolves to one (lowest id) but the result reads as a tie.
    this.matchTie = false;
    // In team mode the result belongs to a side, not to the top scorer: both
    // are recorded, because the standings still rank players inside the teams.
    this.matchWinnerTeam = null;
    this.nextRoundAt = 0;
    this.matchRestartAt = 0;
    this.roundEndsAt = 0;
    this.timeLeftAtEndMs = 0;
    this.matchStandings = [];
    this.roundStartFreezeUntilMs = 0;
    this.roundNumber = 0;

    this.deathEvents = [];
    this.deathSeq = 0;
    this.deathEventsVersion = 0;
    this.standingsVersion = 0;
    this.lastSentVersions = {
      deathEvents: -1,
      standings: -1,
      powerups: -1,
      playerMeta: -1,
      // Signature rather than a counter: the spawn block changes from several
      // call sites (spawn, death, respawn), and a value check cannot be missed
      // the way a forgotten version bump can.
      playerSpawn: '',
      // Same reasoning as playerSpawn, for the per-player booleans that repeat
      // without changing. Measured in an 8-player room at the steering cadence
      // (50 snapshots/s): alive, connected and drawingTrail together cost
      // 403 B/snapshot and changed on 0.0%, 0.2% and 1.4% of ticks. That
      // repetition is a sixth of a snapshot, and snapshot size is what decides
      // whether a tick fits in one TCP segment -- a second segment is a second
      // chance to head-of-line block every snapshot queued behind it.
      //
      // `status` is the other big repeater (268 B/snapshot, 0.0% churn) and is
      // deliberately NOT gated: buildSharedState's result is retained as the
      // room's latestState, and six call sites in room.js read
      // latestState.status for game-over and round-freeze decisions. Gating it
      // would need those readers to cache it first.
      playerFlags: ''
    };
    // Player identity fields (label/color/isBot) are fixed for the lifetime
    // of an engine; snapshots resend them only when this version changes.
    this.playerMetaVersion = 1;
    this.powerups = [];
    this.powerupsActiveCount = 0;
    this.powerupsVersion = 0;
    this.powerupsDirty = false;
    this.powerupSpawnTick = 0;
    this.powerupIdSeq = 0;
    this.clearFieldRequested = false;
    // Debug death captures pause on the collision tick. Keeping a crashed
    // bot's trail in that frozen frame makes the route into the collision
    // inspectable; normal matches retain the existing erase-on-death behavior.
    this.preserveBotTrailOnDeath = options.preserveBotTrailOnDeath === true;
    this.gameId = '';
    this.sizeScale = 1;
    // Per-room balance overrides (host-tunable game settings); anything not
    // supplied falls back to the global BASIC_BALANCE-derived constants, so
    // existing rooms, the bot simulator, and tests are byte-identical.
    const balance = options.balance && typeof options.balance === 'object' ? options.balance : {};
    const num = (value, fallback) => (
      Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback
    );
    this.bikeSpeed = num(balance.bikeSpeed, BIKE_SPEED);
    this.turnRadiusBase = num(balance.turnRadius, TURN_RADIUS);
    this.trailSize = num(balance.trailSize, TRAIL_SIZE) * this.sizeScale;
    this.collisionSize = Math.min(this.trailSize, num(balance.collisionSize, CURVE_COLLISION_SIZE));
    this.collisionRadiusFactor = this.collisionSize / this.trailSize;
    this.collisionOccRadiusInset = (this.trailSize / 2) * (1 - this.collisionRadiusFactor);
    this.holeSize = num(balance.holeSize, HOLE_SIZE);
    this.gapStartChancePerTick = clamp(
      num(balance.holeFrequencyPerSecond, HOLE_FREQUENCY_PER_SECOND) * (TICK_MS / 1000),
      0,
      1
    );
    this.powerupSpawnFrequencyPerSecond = num(
      balance.powerupSpawnFrequencyPerSecond,
      POWERUP_FREQUENCY_PER_SECOND
    );
    this.powerupSpawnChancePerTick = clamp(this.powerupSpawnFrequencyPerSecond * (TICK_MS / 1000), 0, 1);
    this.respawnTrailGapDistance = this.bikeSpeed * FLY_GRACE_SECONDS;
    this.powerupRadius = num(balance.powerupRadius, POWERUP_RADIUS) * this.sizeScale;
    this.powerupGrabRadius = this.powerupRadius;
    this.powerupPlayerSafeRadius = POWERUP_PLAYER_SAFE_RADIUS * this.sizeScale;
    this.powerupWallMargin = this.powerupRadius;

    this.tickOps = [];
    this.historyOps = [];
    this._updates = [];
    this.onPowerupGrab = typeof options.onPowerupGrab === 'function'
      ? options.onPowerupGrab
      : null;
    // Bot intelligence lives server-side (server/bot-logic.js) and is
    // injected via options.createBotLogic; without it bots sail straight.
    const createBotLogicFn = typeof options.createBotLogic === 'function'
      ? options.createBotLogic
      : () => ({ getDirBot: () => 0, observeHuman: () => {} });
    this.botLogic = createBotLogicFn({
        occupied: this.occupied.bind(this),
        // Exact per-sample equivalent of the trail-death check in moveTick
        // (same radius, self-grace, and inset), so bot rollouts can probe
        // the first few steps the boolean grid cannot age-discriminate.
        occupiedWithSelfGrace: (x, y, selfOwner) => this.occupiedDiscWithGrace(
          x,
          y,
          this.trailSize / 2,
          selfOwner,
          COLLISION_SELF_GRACE_TICKS,
          this.collisionOccRadiusInset
        ),
        // Curvefish deliberately overlooks only the tiny live tail directly
        // behind its head. Using the engine's full collision grace here would
        // make planned loops look safe until they mature into lethal trail.
        occupiedForBot: (x, y, selfOwner) => this.occupiedDiscWithGrace(
          x,
          y,
          this.trailSize / 2,
          selfOwner,
          Math.min(2, COLLISION_SELF_GRACE_TICKS),
          this.collisionOccRadiusInset
        ),
        // Swept-capsule equivalent of occupiedForBot over one movement step.
        // Deep survival rollouts need engine-density collision fidelity; the
        // capsule reads each covered cell once instead of once per sample.
        occupiedSweptForBot: (x1, y1, x2, y2, selfOwner) => this.occupiedSegmentWithGrace(
          x1,
          y1,
          x2,
          y2,
          this.trailSize / 2,
          selfOwner,
          Math.min(2, COLLISION_SELF_GRACE_TICKS),
          this.collisionOccRadiusInset
        ),
        // Read-only arena occupancy lets Curvefish distinguish connected open
        // space and collision-clear direct gem approaches from grid detours.
        occupancyMap: () => ({
          cells: this.occ,
          width: this.occW,
          height: this.occH,
          resolution: FIELD_RES
        }),
        // Sampled trail-ownership density in a disc: how much of the
        // neighborhood is walled, split into the caller's own trail vs
        // everything else. selfOwner uses the same playerId+1 convention as
        // the occupancy probes. Lets the policy price "target sits inside my
        // own web" — plain occupancy cannot see whose walls they are.
        trailDensityDisc: (x, y, radius, selfOwner) => {
          const step = Math.max(1 / FIELD_RES, radius / 4);
          let own = 0;
          let other = 0;
          let total = 0;
          for (let dy = -radius; dy <= radius; dy += step) {
            for (let dx = -radius; dx <= radius; dx += step) {
              if (dx * dx + dy * dy > radius * radius) continue;
              const xi = Math.round((x + dx) * FIELD_RES);
              const yi = Math.round((y + dy) * FIELD_RES);
              if (xi < 0 || yi < 0 || xi >= this.occW || yi >= this.occH) continue;
              total += 1;
              const idx = yi * this.occW + xi;
              if (this.occ[idx] !== 1) continue;
              if (this.occOwner[idx] === selfOwner) own += 1;
              else other += 1;
            }
          }
          return { own, other, total };
        },
        effectState: this.effectState.bind(this),
        fieldWidth: this.fieldWidth,
        fieldHeight: this.fieldHeight,
        trailSize: this.trailSize,
        powerupRadius: this.powerupRadius,
        bikeSpeed: this.bikeSpeed,
        turnRadius: this.turnRadiusBase,
        tickMs: TICK_MS,
        powerups: () => this.powerups,
        players: () => this.players,
        tickCounter: () => this.tickCounter,
        lastTickMs: () => this.lastTickMs,
        seed: () => this.seed,
        gameId: () => this.gameId,
        roundNumber: () => this.roundNumber,
        scores: () => this.scores
      }) || { getDirBot: () => 0, observeHuman: () => { } };

    this.resetMatch(Date.now());
  }

  randomRange(min, max) {
    return min + this.random() * (max - min);
  }

  randomChance(probability) {
    return this.random() < probability;
  }

  getConfig() {
    return {
      fieldWidth: this.fieldWidth,
      fieldHeight: this.fieldHeight,
      tickMs: TICK_MS,
      humanCount: this.humanCount,
      botCount: this.botCount,
      seed: this.seed,
      speedEffectTicks: SPEED_EFFECT_TICKS,
      speedStackBonus: SPEED_STACK_SPEED_BONUS,
      speedTurnRadiusMul: SPEED_STACK_TURN_RADIUS_MUL,
      // JSON has no Infinity, so an uncapped stack limit goes out as 0 and the
      // client reads 0/absent as "no limit" (see speedMaxStacks in effects.js).
      speedMaxStacks: Number.isFinite(SPEED_STACK_MAX) ? SPEED_STACK_MAX : 0,
      trailSize: this.trailSize,
      powerupRadius: this.powerupRadius,
      powerupSpawnFrequencyPerSecond: this.powerupSpawnFrequencyPerSecond,
      powerupWeights: { ...GEM_MODE_POWERUP_WEIGHTS },
      gemRoundDurationMs: GEM_ROUND_DURATION_MS,
      respawnDelayMs: RESPAWN_DELAY_MS,
      respawnSafeMs: FLY_GRACE_MS,
      firstToPercent: BASIC_BALANCE.firstToPercent,
      pointsPerSurvive: POINTS_PER_SURVIVE,
      pointsPerKill: POINTS_PER_KILL,
      eraseDeadLines: ERASE_DEAD_LINES,
      alwaysActivePowerups: ALWAYS_ACTIVE_POWERUPS.slice(),
      sizeScale: this.sizeScale,
      bikeSpeed: this.bikeSpeed,
      turnRadius: this.turnRadiusBase,
      steerRampMs: STEER_RAMP_MS,
      colors: COLORS,
      controls: { left: 'ArrowLeft', right: 'ArrowRight', display: 'LEFT / RIGHT' }
    };
  }

  setHumanInput(input) {
    this.setPlayerInput(0, input);
  }

  setPlayerInput(playerId, input) {
    if (!input || typeof input !== 'object') return;
    this.setPlayerInputRaw(playerId, input.left, input.right);
  }

  setPlayerInputRaw(playerId, left, right) {
    const id = Number.parseInt(playerId, 10);
    if (!Number.isFinite(id) || id < 0) return;
    const existing = this.playerInputs.get(id);
    if (existing) {
      existing.left = Boolean(left);
      existing.right = Boolean(right);
      return;
    }
    this.playerInputs.set(id, {
      left: Boolean(left),
      right: Boolean(right)
    });
  }

  turnRadiusForPlayer(playerId) {
    const id = Number.parseInt(playerId, 10);
    const radius = Number.isFinite(id) && id >= 0
      ? Number(this.playerTurnRadii[id])
      : NaN;
    return Number.isFinite(radius) && radius > 0 ? radius : this.turnRadiusBase;
  }

  setPlayerTurnRadius(playerId, radius) {
    const id = Number.parseInt(playerId, 10);
    const parsedRadius = Number(radius);
    if (!Number.isFinite(id) || id < 0 || !Number.isFinite(parsedRadius) || parsedRadius <= 0) return;
    this.playerTurnRadii[id] = parsedRadius;
  }

  clearPlayerInput(playerId) {
    const id = Number.parseInt(playerId, 10);
    if (!Number.isFinite(id) || id < 0) return;
    this.setPlayerInputRaw(id, false, false);
  }

  // Marks a player's owner as (dis)connected so their curve greys out while
  // they are gone and returns to full color when they resume. Purely cosmetic:
  // it does not touch simulation, scores, or the player's real color.
  setPlayerConnected(playerId, connected) {
    const id = Number.parseInt(playerId, 10);
    if (!Number.isFinite(id) || id < 0) return;
    if (connected) {
      this.disconnectedPlayers.delete(id);
    } else {
      this.disconnectedPlayers.add(id);
    }
  }

  // The color a player's freshly painted trail should use: grey while their
  // owner is disconnected, otherwise the player's real color.
  trailColorFor(player) {
    if (player && this.disconnectedPlayers.has(player.id)) return DISCONNECTED_COLOR;
    return player?.color;
  }

  setAllPlayerInputs(inputMap) {
    if (!inputMap || typeof inputMap !== 'object') return;
    for (const [idRaw, input] of Object.entries(inputMap)) {
      this.setPlayerInput(Number.parseInt(idRaw, 10), input);
    }
  }

  setPlayerCounts(options = {}, now = Date.now()) {
    const parsedHumanCount = Number.parseInt(options.humanCount, 10);
    const parsedBotCount = Number.parseInt(options.botCount, 10);
    const nextHumanCount = Number.isFinite(parsedHumanCount) ? Math.max(0, parsedHumanCount) : this.humanCount;
    const nextBotCount = Number.isFinite(parsedBotCount) ? Math.max(0, parsedBotCount) : this.botCount;
    if (nextHumanCount === this.humanCount && nextBotCount === this.botCount) return;
    this.humanCount = nextHumanCount;
    this.botCount = nextBotCount;
    this.playerInputs.clear();
    for (let i = 0; i < this.humanCount; i++) {
      this.playerInputs.set(i, { left: false, right: false });
    }
    this.resetMatch(now);
  }

  setSeed(seed, now = Date.now()) {
    const parsed = Number(seed);
    if (!Number.isFinite(parsed)) return;
    this.seed = parsed >>> 0;
    this.random.setState(this.seed);
    this.resetMatch(now);
  }

  requestReset(now = Date.now()) {
    this.resetMatch(now);
  }

  togglePause(now = Date.now()) {
    if (this.gameOver) return;

    if (this.manualPaused) {
      this.manualPaused = false;
      if (!this.waitingNext && !this.gameOver) {
        this.running = true;
      }
      return;
    }

    this.manualPaused = true;
    this.running = false;
  }

  update(now = Date.now(), inputFrames = null) {
    this.lastTickMs = now;
    if (this.manualPaused) return;

    if (this.gameOver) {
      return;
    }

    if (this.waitingNext) {
      if (this.nextRoundAt > 0 && now >= this.nextRoundAt) {
        this.beginRound(now);
      }
    }

    if (this.running) {
      if (now < this.roundStartFreezeUntilMs) return;
      if (this.roundEndsAt > 0 && now >= this.roundEndsAt) {
        this.finalizeTimedMatch(now);
        return;
      }
      this.tick(inputFrames);
      if (this.roundEndsAt > 0 && now >= this.roundEndsAt) {
        this.finalizeTimedMatch(now);
      }
    }
  }

  // Ends a timed match now instead of when the clock runs out. It pulls the
  // deadline back rather than finalizing here, so the match ends through the
  // one existing path (finalizeTimedMatch, off the step loop) and the scoring,
  // the winner and the end overlay are identical to running the clock down.
  // Returns false when there is nothing to end.
  endTimedMatchNow(nowMs = 0) {
    if (!this.running || this.gameOver || this.roundEndsAt <= 0) return false;
    const now = Number.isFinite(Number(nowMs)) && Number(nowMs) > 0
      ? Number(nowMs)
      : (this.lastTickMs || Date.now());
    // Already ending, or the clock ran out on its own: there is nothing left to
    // stop, and returning here keeps a second call from recomputing the frozen
    // reading against the deadline the first one already pulled back -- which
    // would report zero and lose the time the player actually stopped at.
    if (this.roundEndsAt <= now) return false;
    // What the clock read at the moment it was stopped. The display freezes
    // here instead of falling to 0:00, which would claim the match ran out.
    this.timeLeftAtEndMs = this.roundEndsAt - now;
    this.roundEndsAt = now;
    return true;
  }

  getRoundStartFreezeDurationMs() {
    let maxSpawnDelayMs = 0;
    for (const p of this.players) {
      maxSpawnDelayMs = Math.max(maxSpawnDelayMs, p.spawnDelayMs || 0);
    }

    const spawnFxMs = HEAD_SPAWN_TIMER_MS + maxSpawnDelayMs + HEAD_SPAWN_SCALE_DELAY_MS + HEAD_SPAWN_SCALE_MS;
    return Math.max(spawnFxMs, HEAD_SPAWN_MARKER_MS);
  }

  buildInitPayload(now = Date.now()) {
    this.lastSentVersions.deathEvents = this.deathEventsVersion;
    this.lastSentVersions.standings = this.standingsVersion;
    return {
      type: 'init',
      config: this.getConfig(),
      historyOps: this.historyOps,
      state: this.buildSharedState(now, { includeDeathEvents: true, includeStandings: true }),
      ops: []
    };
  }

  drainTickOps() {
    const ops = this.tickOps;
    this.tickOps = [];
    return ops;
  }

  buildTickPayload(now = Date.now(), options = {}) {
    // commitVersions=false builds a payload without marking the versioned
    // sections as sent (for states that are kept locally, not broadcast).
    const commitVersions = options.commitVersions !== false;
    const includeDeathEvents = this.lastSentVersions.deathEvents !== this.deathEventsVersion;
    const includeStandings = this.lastSentVersions.standings !== this.standingsVersion;
    const includePowerups = this.lastSentVersions.powerups !== this.powerupsVersion;
    const includePlayerMeta = this.lastSentVersions.playerMeta !== this.playerMetaVersion;
    const spawnSignature = this.playerSpawnSignature();
    const includePlayerSpawn = this.lastSentVersions.playerSpawn !== spawnSignature;
    const flagsSignature = this.playerFlagsSignature();
    const includePlayerFlags = this.lastSentVersions.playerFlags !== flagsSignature;
    const ops = this.tickOps;
    this.tickOps = [];
    const payload = {
      type: 'state',
      state: this.buildSharedState(now, {
        includeDeathEvents,
        includeStandings,
        includePowerups,
        includePlayerMeta,
        includePlayerSpawn,
        includePlayerFlags
      }),
      ops
    };
    if (commitVersions) {
      this.lastSentVersions.deathEvents = this.deathEventsVersion;
      this.lastSentVersions.standings = this.standingsVersion;
      this.lastSentVersions.powerups = this.powerupsVersion;
      this.lastSentVersions.playerMeta = this.playerMetaVersion;
      this.lastSentVersions.playerSpawn = spawnSignature;
      this.lastSentVersions.playerFlags = flagsSignature;
    }
    return payload;
  }

  playerSpawnFields(p) {
    return {
      respawnAtMs: Number(p.respawnAtMs) || 0,
      spawnAtMs: p.spawnAtMs,
      spawnDelayMs: p.spawnDelayMs,
      // Computed by repeated multiplication, so it arrives as 2.0999999999999996
      // -- 35 characters for a value the spawn animation reads as 2.1.
      spawnScaleStart: wirePx(p.spawnScaleStart),
      spawnMarkerUntilMs: p.spawnMarkerUntilMs
    };
  }

  playerSpawnSignature() {
    let sig = '';
    for (const p of this.players) {
      const f = this.playerSpawnFields(p);
      sig += `${p.id}:${f.respawnAtMs},${f.spawnAtMs},${f.spawnDelayMs},${f.spawnScaleStart},${f.spawnMarkerUntilMs};`;
    }
    return sig;
  }

  // The three per-player booleans that almost never move. Kept as one block so
  // a change to any of them resends all three, which is what lets the client
  // treat "absent" as "unchanged" without tracking them individually.
  playerFlagFields(p) {
    return {
      alive: p.alive,
      connected: !this.disconnectedPlayers.has(p.id),
      drawingTrail: Boolean(
        p.alive
        && !this.isPlayerFlying(p)
        && p.holeDistanceLeft <= 0
        && p.lastEmptyTick < this.tickCounter
      )
    };
  }

  playerFlagsSignature() {
    let sig = '';
    for (const p of this.players) {
      const f = this.playerFlagFields(p);
      sig += `${p.id}:${f.alive ? 1 : 0}${f.connected ? 1 : 0}${f.drawingTrail ? 1 : 0};`;
    }
    return sig;
  }

  buildSharedState(now = Date.now(), options = {}) {
    const includeDeathEvents = options.includeDeathEvents !== false;
    const includeStandings = options.includeStandings !== false;
    const includePowerups = options.includePowerups !== false;
    const includePlayerMeta = options.includePlayerMeta !== false;
    const includePlayerSpawn = options.includePlayerSpawn !== false;
    const includePlayerFlags = options.includePlayerFlags !== false;
    this.compactPowerupsIfNeeded();

    // Powerups (heavy: icon paths, colors) go out only when powerupsVersion
    // moved since the last committed payload; clients cache the previous list.
    let powerups = null;
    if (includePowerups) {
      powerups = [];
      for (const pu of this.powerups) {
        if (!pu || !pu.active) continue;
        powerups.push({
          id: pu.id,
          type: pu.type,
          x: pu.x,
          y: pu.y,
          spawnTick: pu.spawnTick,
          iconMode: pu.iconMode,
          iconSrc: pu.iconSrc,
          variant: typeof pu?.variant === 'string' ? pu.variant : '',
          value: Number.isFinite(Number(pu?.value)) ? Number(pu.value) : 0,
          colors: pu.colors
        });
      }
    }

    const state = {
      serverNow: now,
      tick: this.tickCounter,
      target: this.getMatchTarget(this.players.length),
      scores: this.scores,
      roundScores: this.roundScores,
      powerupsVersion: this.powerupsVersion,
      players: this.players.map((p) => ({
        id: p.id,
        ...(includePlayerMeta
          ? {
            label: p.label,
            color: p.color,
            isBot: p.isBot,
            // Static for the match, so it rides with the rest of the metadata
            // rather than going out 20x a second.
            ...(p.team ? { team: p.team } : {}),
            // Only present for mesh picks, so solids cost nothing on the wire.
            ...(p.mesh ? { mesh: p.mesh } : {})
          }
          : {}),
        x: wirePx(p.x),
        y: wirePx(p.y),
        angle: wireRad(p.angle),
        ...(includePlayerFlags ? this.playerFlagFields(p) : {}),
        ...(this.isPlayerFlying(p) ? { isFlying: true } : {}),
        ...(p.holeDistanceLeft > 0 ? { isGapping: true } : {}),
        ...(Math.max(0, Math.floor(Number(p.flyTicks) || 0)) > 0
          ? { flyTicks: Math.max(0, Math.floor(Number(p.flyTicks) || 0)) }
          : {}),
        ...(p.effect?.type
          ? {
            effect: {
              type: p.effect.type,
              ticks: p.effect?.ticks || 0,
              stacks: p.effect?.stacks || 0
            }
          }
          : {}),
        // The spawn block only moves on spawn and death, so it rides a dirty
        // check instead of going out 20x a second. Everything below is omitted
        // at its default value; the client's hydrateSnapshotState restores a
        // complete player object, so no reader downstream sees a new shape.
        ...(includePlayerSpawn ? this.playerSpawnFields(p) : {})
      })),
      status: {
        gameId: this.gameId,
        roundNumber: this.roundNumber,
        running: this.running,
        waitingNext: this.waitingNext,
        gameOver: this.gameOver,
        paused: this.manualPaused,
        roundWinnerId: this.roundWinnerId,
        matchWinnerId: this.matchWinnerId,
        matchTie: this.matchTie,
        ...(this.teamMode
          ? { teamMode: true, teamScores: this.teamScores(), matchWinnerTeam: this.matchWinnerTeam }
          : {}),
        nextRoundAt: this.nextRoundAt,
        matchRestartAt: this.matchRestartAt,
        roundEndsAt: this.roundEndsAt,
        roundStartFreezeUntilMs: this.roundStartFreezeUntilMs,
        // Only present when a match was stopped early, so a natural finish
        // stays exactly as it was on the wire and still reads 0:00.
        ...(this.timeLeftAtEndMs > 0 ? { timeLeftAtEndMs: this.timeLeftAtEndMs } : {})
      }
    };

    if (includePowerups) {
      state.powerups = powerups;
    }

    if (includeStandings) {
      state.standings = this.matchStandings;
    }

    if (includeDeathEvents) {
      state.deathEvents = this.deathEvents;
    }

    return state;
  }

  exportState() {
    this.compactPowerupsIfNeeded();
    return {
      seed: this.seed,
      randomState: this.random.getState(),
      botCount: this.botCount,
      humanCount: this.humanCount,
      tickCounter: this.tickCounter,
      lastTickMs: this.lastTickMs,
      running: this.running,
      waitingNext: this.waitingNext,
      gameOver: this.gameOver,
      manualPaused: this.manualPaused,
      roundWinnerId: this.roundWinnerId,
      matchWinnerId: this.matchWinnerId,
      matchTie: this.matchTie,
      nextRoundAt: this.nextRoundAt,
      matchRestartAt: this.matchRestartAt,
      roundEndsAt: this.roundEndsAt,
      matchStandings: this.matchStandings,
      roundStartFreezeUntilMs: this.roundStartFreezeUntilMs,
      deathEvents: this.deathEvents,
      deathSeq: this.deathSeq,
      deathEventsVersion: this.deathEventsVersion,
      standingsVersion: this.standingsVersion,
      powerups: this.powerups,
      powerupsVersion: this.powerupsVersion,
      powerupSpawnTick: this.powerupSpawnTick,
      powerupIdSeq: this.powerupIdSeq,
      clearFieldRequested: this.clearFieldRequested,
      tickOps: this.tickOps,
      historyOps: this.historyOps,
      players: this.players,
      scores: this.scores,
      roundScores: this.roundScores,
      gameId: this.gameId,
      roundNumber: this.roundNumber,
      occ: Array.from(this.occ),
      occOwner: Array.from(this.occOwner),
      occTick: Array.from(this.occTick),
      playerInputs: Array.from(this.playerInputs.entries()).map(([id, input]) => ({
        id,
        left: Boolean(input?.left),
        right: Boolean(input?.right)
      })),
      playerTurnRadii: this.playerTurnRadii.slice()
    };
  }

  importState(snapshot = {}) {
    if (!snapshot || typeof snapshot !== 'object') return;
    if (Number.isFinite(Number(snapshot.seed))) {
      this.seed = Number(snapshot.seed) >>> 0;
    }
    if (Number.isFinite(Number(snapshot.randomState))) {
      this.random.setState(Number(snapshot.randomState) >>> 0);
    } else {
      this.random.setState(this.seed);
    }

    this.botCount = Number.isFinite(Number(snapshot.botCount))
      ? Math.max(0, Number.parseInt(snapshot.botCount, 10))
      : this.botCount;
    this.humanCount = Number.isFinite(Number(snapshot.humanCount))
      ? Math.max(0, Number.parseInt(snapshot.humanCount, 10))
      : this.humanCount;

    this.tickCounter = Number(snapshot.tickCounter) || 0;
    if (Number.isFinite(Number(snapshot.lastTickMs))) {
      this.lastTickMs = Number(snapshot.lastTickMs);
    }
    if (typeof snapshot.gameId === 'string' && snapshot.gameId) {
      this.gameId = snapshot.gameId.slice(0, 64);
    }
    const snapshotRoundNumber = Number(snapshot.roundNumber ?? snapshot.status?.roundNumber);
    if (Number.isFinite(snapshotRoundNumber)) {
      this.roundNumber = Math.max(0, Math.floor(snapshotRoundNumber));
    }
    this.running = Boolean(snapshot.running);
    this.waitingNext = Boolean(snapshot.waitingNext);
    this.gameOver = Boolean(snapshot.gameOver);
    this.manualPaused = Boolean(snapshot.manualPaused);
    const roundWinnerId = Number(snapshot.roundWinnerId);
    const matchWinnerId = Number(snapshot.matchWinnerId);
    this.roundWinnerId = Number.isFinite(roundWinnerId) ? roundWinnerId : -1;
    this.matchWinnerId = Number.isFinite(matchWinnerId) ? matchWinnerId : -1;
    this.matchTie = Boolean(snapshot.matchTie);
    this.nextRoundAt = Number(snapshot.nextRoundAt) || 0;
    this.matchRestartAt = Number(snapshot.matchRestartAt) || 0;
    this.roundEndsAt = Number(snapshot.roundEndsAt ?? snapshot.status?.roundEndsAt) || 0;
    this.timeLeftAtEndMs = Number(snapshot.timeLeftAtEndMs ?? snapshot.status?.timeLeftAtEndMs) || 0;
    this.matchStandings = Array.isArray(snapshot.matchStandings) ? snapshot.matchStandings : [];
    this.roundStartFreezeUntilMs = Number(snapshot.roundStartFreezeUntilMs) || 0;
    this.deathEvents = Array.isArray(snapshot.deathEvents) ? snapshot.deathEvents : [];
    this.deathSeq = Number(snapshot.deathSeq) || 0;
    this.deathEventsVersion = Number(snapshot.deathEventsVersion) || 0;
    this.standingsVersion = Number(snapshot.standingsVersion) || 0;
    this.powerups = Array.isArray(snapshot.powerups) ? snapshot.powerups : [];
    this.powerupsVersion = Number(snapshot.powerupsVersion) || 0;
    this.powerupsActiveCount = 0;
    this.powerupsDirty = false;
    if (this.powerups.length > 0) {
      let write = 0;
      for (let i = 0; i < this.powerups.length; i += 1) {
        const pu = this.powerups[i];
        if (pu && pu.active) {
          this.powerups[write] = pu;
          write += 1;
          this.powerupsActiveCount += 1;
        }
      }
      if (write < this.powerups.length) {
        this.powerups.length = write;
      }
    }
    this.powerupSpawnTick = Number(snapshot.powerupSpawnTick) || 0;
    this.powerupIdSeq = Number(snapshot.powerupIdSeq) || 0;
    this.clearFieldRequested = Boolean(snapshot.clearFieldRequested);
    this.tickOps = Array.isArray(snapshot.tickOps) ? snapshot.tickOps : [];
    this.historyOps = Array.isArray(snapshot.historyOps) ? snapshot.historyOps : [];
    this.players = Array.isArray(snapshot.players) ? snapshot.players : [];
    this.scores = Array.isArray(snapshot.scores) ? snapshot.scores : [];
    this.roundScores = Array.isArray(snapshot.roundScores) ? snapshot.roundScores : [];
    if (Array.isArray(snapshot.playerTurnRadii)) {
      this.playerTurnRadii = snapshot.playerTurnRadii.map((radius) => (
        Number.isFinite(Number(radius)) && Number(radius) > 0 ? Number(radius) : null
      ));
    }

    if (snapshot.occ && snapshot.occ.length === this.occ.length) {
      this.occ.set(snapshot.occ);
      this.rebuildOccBlocks();
    }
    if (snapshot.occOwner && snapshot.occOwner.length === this.occOwner.length) {
      this.occOwner.set(snapshot.occOwner);
    }
    if (snapshot.occTick && snapshot.occTick.length === this.occTick.length) {
      this.occTick.set(snapshot.occTick);
    }
    this.rebuildOccBlocksByOwner();

    this.playerInputs.clear();
    if (Array.isArray(snapshot.playerInputs) && snapshot.playerInputs.length > 0) {
      for (const entry of snapshot.playerInputs) {
        const id = Number.parseInt(entry?.id, 10);
        if (!Number.isFinite(id) || id < 0) continue;
        this.playerInputs.set(id, { left: Boolean(entry?.left), right: Boolean(entry?.right) });
      }
    } else {
      for (let i = 0; i < this.humanCount; i++) {
        this.playerInputs.set(i, { left: false, right: false });
      }
    }
  }

  getMatchTarget(playerCount = this.humanCount + this.botCount) {
    return matchTarget(playerCount);
  }

  getPlayerDir(playerId = 0) {
    const input = this.playerInputs.get(playerId) || { left: false, right: false };
    const left = input.left;
    const right = input.right;
    if (left && !right) return -1;
    if (right && !left) return 1;
    return 0;
  }

  applyHumanInputFrame(playerInputs = null) {
    if (!Array.isArray(playerInputs)) return;
    for (const entry of playerInputs) {
      const playerId = Number.parseInt(entry?.playerId, 10);
      if (!Number.isFinite(playerId) || playerId < 0 || playerId >= this.humanCount) continue;
      this.setPlayerInputRaw(playerId, Boolean(entry?.left), Boolean(entry?.right));
    }
  }

  normalizeTickInputFrames(inputFrames = null) {
    if (!Array.isArray(inputFrames) || inputFrames.length === 0) {
      return [{ dtMs: TICK_MS, playerInputs: null }];
    }

    const frames = [];
    let remainingMs = TICK_MS;
    for (const rawFrame of inputFrames) {
      if (remainingMs <= TICK_FRAME_EPSILON_MS) break;
      const rawDtMs = Number(rawFrame?.dtMs);
      if (!Number.isFinite(rawDtMs) || rawDtMs <= TICK_FRAME_EPSILON_MS) continue;
      const dtMs = clamp(rawDtMs, 0, remainingMs);
      if (dtMs <= TICK_FRAME_EPSILON_MS) continue;
      frames.push({
        dtMs,
        playerInputs: Array.isArray(rawFrame?.playerInputs) ? rawFrame.playerInputs : null
      });
      remainingMs -= dtMs;
    }

    if (frames.length === 0) {
      return [{ dtMs: TICK_MS, playerInputs: null }];
    }
    if (remainingMs > TICK_FRAME_EPSILON_MS) {
      frames.push({ dtMs: remainingMs, playerInputs: null });
    }
    return frames;
  }

  nextPowerupSpawnTick(firstSpawn = false) {
    if (firstSpawn) return this.tickCounter + POWERUP_FIRST_SPAWN_TICKS;
    if (this.powerupSpawnChancePerTick >= 1) return this.tickCounter + 1;
    const roll = Math.max(Number.EPSILON, this.random());
    const delayTicks = Math.max(
      1,
      Math.ceil(Math.log(1 - roll) / Math.log(1 - this.powerupSpawnChancePerTick))
    );
    return this.tickCounter + delayTicks;
  }

  pickGemVariant() {
    const roll = this.randomRange(0, GEM_VARIANT_WEIGHT_TOTAL);
    let cursor = 0;
    for (const variant of GEM_VARIANTS) {
      cursor += variant.weight;
      if (roll < cursor) return variant;
    }
    return GEM_VARIANTS[0];
  }

  effectState(player) {
    const effect = player.effect || null;
    if (!effect || effect.ticks <= 0 || !effect.type) {
      return {
        speedMul: 1,
        turnRadiusMul: 1
      };
    }

    if (effect.type === PU_TYPES.SPEED) {
      const stacks = clamp(
        Math.max(1, Math.floor(Number(effect.stacks) || 0)),
        1,
        SPEED_STACK_MAX
      );
      const speedMul = Math.pow(1 + SPEED_STACK_SPEED_BONUS, stacks);
      const turnRadiusMul = Math.pow(SPEED_STACK_TURN_RADIUS_MUL, stacks);
      return {
        speedMul,
        turnRadiusMul
      };
    }

    return {
      speedMul: 1,
      turnRadiusMul: 1
    };
  }

  bumpPowerupsVersion() {
    this.powerupsVersion = (Number(this.powerupsVersion) + 1) >>> 0;
  }

  compactPowerupsIfNeeded() {
    if (!this.powerupsDirty) return;
    const powerups = this.powerups;
    let write = 0;
    for (let i = 0; i < powerups.length; i += 1) {
      const pu = powerups[i];
      if (pu && pu.active) {
        powerups[write] = pu;
        write += 1;
      }
    }
    if (write < powerups.length) {
      powerups.length = write;
    }
    this.powerupsDirty = false;
  }

  spawnPowerup() {
    if (this.powerupsActiveCount >= POWERUP_MAX_ACTIVE) {
      return false;
    }

    const minX = this.powerupWallMargin;
    const maxX = this.fieldWidth - this.powerupWallMargin;
    const minY = this.powerupWallMargin;
    const maxY = this.fieldHeight - this.powerupWallMargin;
    const minPlayerDistSq = this.powerupPlayerSafeRadius * this.powerupPlayerSafeRadius;
    const minPowerupDist = this.powerupRadius * 2.2;
    const minPowerupDistSq = minPowerupDist * minPowerupDist;
    const occProbeRadius = Math.max(OCC_STEP * 1.5, this.powerupRadius * 0.78);

    for (let attempt = 0; attempt < 48; attempt++) {
      const x = this.randomRange(minX, maxX);
      const y = this.randomRange(minY, maxY);

      if (this.occupiedDiscWithGrace(x, y, occProbeRadius, 0, 0, 0)) continue;

      let blocked = false;
      for (const p of this.players) {
        if (!p.alive) continue;
        const dx = p.x - x;
        const dy = p.y - y;
        if (dx * dx + dy * dy < minPlayerDistSq) {
          blocked = true;
          break;
        }
      }
      if (blocked) continue;

      for (const pu of this.powerups) {
        if (!pu.active) continue;
        const dx = pu.x - x;
        const dy = pu.y - y;
        if (dx * dx + dy * dy < minPowerupDistSq) {
          blocked = true;
          break;
        }
      }
      if (blocked) continue;

      const typeRoll = this.randomRange(0, POWERUP_SPAWN_WEIGHT_TOTAL);
      const type = typeRoll < GEM_MODE_POWERUP_WEIGHTS.GEM
        ? PU_TYPES.GEM
        : (typeRoll < GEM_MODE_POWERUP_WEIGHTS.GEM + GEM_MODE_POWERUP_WEIGHTS.SPEED
          ? PU_TYPES.SPEED
          : PU_TYPES.CLEAR);
      const def = PU_DEF[type] || PU_DEF.SPEED;
      const gemVariant = type === PU_TYPES.GEM ? this.pickGemVariant() : null;

      this.powerups.push({
        id: ++this.powerupIdSeq,
        type,
        x,
        y,
        active: true,
        spawnTick: this.tickCounter,
        iconMode: def.iconMode || 'image',
        iconSrc: def.iconSrc || '',
        variant: gemVariant?.name || '',
        value: Number.isFinite(Number(gemVariant?.value)) ? Number(gemVariant.value) : 0,
        colors: Array.isArray(gemVariant?.colors)
          ? gemVariant.colors.slice(0, 4)
          : (Array.isArray(def.colors) ? def.colors.slice(0, 4) : null)
      });
      this.powerupsActiveCount += 1;
      this.bumpPowerupsVersion();
      return true;
    }

    return false;
  }

  applyPowerup(player, type, powerup = null) {
    if (type === PU_TYPES.GEM) {
      const playerId = Number.parseInt(player?.id, 10);
      if (!Number.isFinite(playerId) || playerId < 0) return;
      const value = Number.isFinite(Number(powerup?.value))
        ? Math.max(1, Math.floor(Number(powerup.value)))
        : GEM_VARIANTS[0].value;
      this.scores[playerId] = (Number(this.scores[playerId]) || 0) + value;
      this.roundScores[playerId] = (Number(this.roundScores[playerId]) || 0) + value;
      const activity = this.matchActivityFor(playerId);
      if (activity) activity.gems += 1;
      return;
    }

    if (type === PU_TYPES.CLEAR) {
      this.clearFieldRequested = true;
      return;
    }

    if (type === PU_TYPES.SPEED) {
      // Each pickup carries its own timer, the way CF2/CurveCrash do it: they
      // multiply on take and divide on expiry, so boosts overlap and fall away
      // one at a time. Refreshing a single shared timer instead would drop the
      // whole stack at once -- 8x straight back to 1x in one tick.
      const current = player.effect || null;
      const expiries = (current && current.type === PU_TYPES.SPEED && Array.isArray(current.expiries))
        ? current.expiries.filter((ticks) => ticks > 0)
        : [];
      expiries.push(SPEED_EFFECT_TICKS);
      if (expiries.length > SPEED_STACK_MAX) {
        // Over the cap, retire whichever boost was closest to ending, so a
        // fresh pickup always counts for something.
        expiries.sort((a, b) => a - b);
        expiries.splice(0, expiries.length - SPEED_STACK_MAX);
      }
      player.effect = {
        type: PU_TYPES.SPEED,
        // The wire and the HUD rings track one countdown, so publish the
        // longest-lived stack: the rings then empty as the boost really does.
        ticks: Math.max(...expiries),
        stacks: expiries.length,
        expiries
      };
    }
  }

  processPowerupGrab(player) {
    const radius = this.powerupGrabRadius + this.trailSize * 0.5;
    const radiusSq = radius * radius;

    for (const pu of this.powerups) {
      if (!pu.active) continue;
      const dx = pu.x - player.x;
      const dy = pu.y - player.y;
      if (dx * dx + dy * dy > radiusSq) continue;

      pu.active = false;
      if (this.powerupsActiveCount > 0) {
        this.powerupsActiveCount -= 1;
      }
      this.powerupsDirty = true;
      this.bumpPowerupsVersion();
      this.applyPowerup(player, pu.type, pu);
      if (this.onPowerupGrab) {
        this.onPowerupGrab({
          playerId: Number(player?.id),
          playerLabel: typeof player?.label === 'string' ? player.label : '',
          type: pu.type,
          variant: typeof pu?.variant === 'string' ? pu.variant : '',
          value: Number.isFinite(Number(pu?.value)) ? Number(pu.value) : 0,
          powerupId: Number(pu?.id),
          tick: Number(this.tickCounter),
          effectType: player?.effect?.type || null,
          effectTicks: Number(player?.effect?.ticks) || 0,
          effectStacks: Number(player?.effect?.stacks) || 0
        });
      }
    }
  }

  tickEffects() {
    for (const p of this.players) {
      const effect = p.effect || null;
      if (!effect || effect.ticks <= 0) continue;

      if (effect.type === PU_TYPES.SPEED && Array.isArray(effect.expiries)) {
        const expiries = [];
        for (const remaining of effect.expiries) {
          const next = remaining - 1;
          if (next > 0) expiries.push(next);
        }
        if (expiries.length === 0) {
          effect.type = null;
          effect.ticks = 0;
          effect.stacks = 0;
          effect.expiries = [];
          continue;
        }
        effect.expiries = expiries;
        effect.stacks = expiries.length;
        effect.ticks = Math.max(...expiries);
        continue;
      }

      effect.ticks -= 1;
      if (effect.ticks <= 0) {
        effect.type = null;
        effect.ticks = 0;
        effect.stacks = 0;
      }
    }
  }

  clearField() {
    this.occ.fill(0);
    this.occOwner.fill(0);
    this.occTick.fill(0);
    this.occBlocks.fill(0);
    this.occBlocksByOwner.clear();
    this.tickOps = [];
    this.historyOps = [];
    this.pushOp({ k: 'c' });
  }

  // Trail ops are render-only -- collision runs off the occupancy grid, which
  // is filled separately from the same unrounded values -- so the wire copy is
  // quantised to the precision a canvas can actually show. Full doubles cost
  // ~18 characters per coordinate against ~6 here, and trail ops are ~42% of a
  // snapshot. Rounding at push keeps historyOps and the live stream identical.
  pushOp(op) {
    const quantised = quantiseTrailOp(op);
    this.tickOps.push(quantised);
    this.historyOps.push(quantised);
  }

  eraseTrailOwner(playerId) {
    const occBW = this.occBW, occH = this.occH, occW = this.occW;
    const publicOwnerId = Number.parseInt(playerId, 10);
    if (!Number.isFinite(publicOwnerId) || publicOwnerId < 0) return;

    const occOwnerId = publicOwnerId + 1;
    const ownerBlockRecord = this.occBlocksByOwner.get(occOwnerId);
    const ownerBlocks = ownerBlockRecord?.blocks || [];
    for (let i = 0; i < ownerBlocks.length; i += 1) {
      const blockIndex = ownerBlocks[i];
      const blockX = blockIndex % occBW;
      const blockY = (blockIndex - blockX) / occBW;
      const minX = blockX << OCC_BLOCK_SHIFT;
      const minY = blockY << OCC_BLOCK_SHIFT;
      const maxX = Math.min(occW, minX + (1 << OCC_BLOCK_SHIFT));
      const maxY = Math.min(occH, minY + (1 << OCC_BLOCK_SHIFT));
      let occupiedCount = 0;
      for (let yy = minY; yy < maxY; yy += 1) {
        const rowBase = yy * occW;
        for (let xx = minX; xx < maxX; xx += 1) {
          const cellIndex = rowBase + xx;
          if (this.occOwner[cellIndex] === occOwnerId) {
            this.occ[cellIndex] = 0;
            this.occOwner[cellIndex] = 0;
            this.occTick[cellIndex] = 0;
          }
          if (this.occ[cellIndex] === 1) occupiedCount += 1;
        }
      }
      this.occBlocks[blockIndex] = occupiedCount;
    }
    this.occBlocksByOwner.delete(occOwnerId);

    this.historyOps = this.historyOps.filter((op) => {
      if (!op || typeof op !== 'object') return true;
      const owner = Number(op.o);
      return !Number.isFinite(owner) || Math.floor(owner) !== publicOwnerId;
    });
    // A crash used to replace the small tick delta with the entire surviving
    // trail history. Late in a round that created multi-megabyte snapshots,
    // blocked input acknowledgements, and forced the browser to redraw every
    // line before it could display a turn. Remove only this owner's unsent
    // segments and emit one transient owner-erasure command instead.
    this.tickOps = this.tickOps.filter((op) => {
      if (!op || (op.k !== 's' && op.k !== 'p')) return true;
      const owner = Number(op.o);
      return !Number.isFinite(owner) || Math.floor(owner) !== publicOwnerId;
    });
    this.tickOps.push({ k: 'r', o: publicOwnerId });
  }

  recordOccCellOwner(cellIndex, owner, blockIndex) {
    if (owner > 0 && this.occOwner[cellIndex] !== owner) {
      let record = this.occBlocksByOwner.get(owner);
      if (!record) {
        record = {
          blocks: [],
          marked: new Uint8Array(this.occBW * this.occBH)
        };
        this.occBlocksByOwner.set(owner, record);
      }
      if (record.marked[blockIndex] === 0) {
        record.marked[blockIndex] = 1;
        record.blocks.push(blockIndex);
      }
    }
    this.occOwner[cellIndex] = owner;
  }

  rebuildOccBlocksByOwner() {
    const occBH = this.occBH, occBW = this.occBW, occH = this.occH, occW = this.occW;
    this.occBlocksByOwner.clear();
    for (let yy = 0; yy < occH; yy += 1) {
      const rowBase = yy * occW;
      const blockRowBase = (yy >> OCC_BLOCK_SHIFT) * occBW;
      for (let xx = 0; xx < occW; xx += 1) {
        const owner = this.occOwner[rowBase + xx];
        if (owner <= 0) continue;
        let record = this.occBlocksByOwner.get(owner);
        if (!record) {
          record = {
            blocks: [],
            marked: new Uint8Array(occBW * occBH)
          };
          this.occBlocksByOwner.set(owner, record);
        }
        const blockIndex = blockRowBase + (xx >> OCC_BLOCK_SHIFT);
        if (record.marked[blockIndex] === 0) {
          record.marked[blockIndex] = 1;
          record.blocks.push(blockIndex);
        }
      }
    }
  }

  fillOccDisc(x, y, radius, owner = 0, stamp = 0) {
    const occBW = this.occBW, occH = this.occH, occW = this.occW;
    const ownerId = Math.max(0, Math.min(255, Math.trunc(Number(owner) || 0)));
    const sr = radius * FIELD_RES;
    const r = Math.ceil(sr);
    const cx = Math.round(x * FIELD_RES);
    const cy = Math.round(y * FIELD_RES);

    for (let dy = -r; dy <= r; dy++) {
      const yy = cy + dy;
      if (yy < 0 || yy >= occH) continue;

      for (let dx = -r; dx <= r; dx++) {
        const xx = cx + dx;
        if (xx < 0 || xx >= occW) continue;
        if (dx * dx + dy * dy > sr * sr) continue;

        const idx = yy * occW + xx;
        const blockIndex = (yy >> OCC_BLOCK_SHIFT) * occBW + (xx >> OCC_BLOCK_SHIFT);
        if (this.occ[idx] === 0) {
          this.occ[idx] = 1;
          this.occBlocks[blockIndex] += 1;
        }
        this.recordOccCellOwner(idx, ownerId, blockIndex);
        this.occTick[idx] = stamp;
      }
    }
  }

  fillOccSegmentButt(x1, y1, x2, y2, radius, owner = 0, stamp = 0) {
    const occBW = this.occBW, occH = this.occH, occW = this.occW;
    const ownerId = Math.max(0, Math.min(255, Math.trunc(Number(owner) || 0)));
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq <= 1e-9) {
      this.fillOccDisc(x1, y1, radius, owner, stamp);
      return;
    }

    const pad = radius + OCC_STEP * 0.75;
    const minX = Math.max(0, Math.floor((Math.min(x1, x2) - pad) * FIELD_RES));
    const maxX = Math.min(occW - 1, Math.ceil((Math.max(x1, x2) + pad) * FIELD_RES));
    const minY = Math.max(0, Math.floor((Math.min(y1, y2) - pad) * FIELD_RES));
    const maxY = Math.min(occH - 1, Math.ceil((Math.max(y1, y2) + pad) * FIELD_RES));
    const radiusSq = radius * radius;

    for (let yy = minY; yy <= maxY; yy++) {
      const py = yy * OCC_STEP;
      for (let xx = minX; xx <= maxX; xx++) {
        const px = xx * OCC_STEP;
        const t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
        if (t < 0 || t > 1) continue;

        const qx = x1 + dx * t;
        const qy = y1 + dy * t;
        const ox = px - qx;
        const oy = py - qy;
        if (ox * ox + oy * oy > radiusSq) continue;

        const idx = yy * occW + xx;
        const blockIndex = (yy >> OCC_BLOCK_SHIFT) * occBW + (xx >> OCC_BLOCK_SHIFT);
        if (this.occ[idx] === 0) {
          this.occ[idx] = 1;
          this.occBlocks[blockIndex] += 1;
        }
        this.recordOccCellOwner(idx, ownerId, blockIndex);
        this.occTick[idx] = stamp;
      }
    }
  }

  paintTrailSegment(x1, y1, x2, y2, radius, color, owner = 0, stamp = 0, fitBack = 0) {
    let sx = x1;
    let sy = y1;

    if (fitBack > 0) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.hypot(dx, dy);
      if (len > 1e-6) {
        const ux = dx / len;
        const uy = dy / len;
        sx -= ux * fitBack;
        sy -= uy * fitBack;
      }
    }

    const ownerId = trailOpOwnerId(owner);
    this.pushOp({
      k: 's',
      x1: sx,
      y1: sy,
      x2,
      y2,
      r: radius,
      c: color,
      ...(ownerId !== null ? { o: ownerId } : {})
    });

    const occRadius = Math.max(OCC_STEP * 1.2, radius * this.collisionRadiusFactor);
    this.fillOccSegmentButt(sx, sy, x2, y2, occRadius, owner, stamp);
  }

  paintTrailSegmentSubsteps(
    x1,
    y1,
    x2,
    y2,
    radius,
    color,
    owner = 0,
    stamp = 0,
    fitBack = 0,
    startAngle = NaN,
    endAngle = NaN
  ) {
    const steps = Math.max(1, Math.floor(TRAIL_DRAW_SUBSTEPS));
    if (steps <= 1 || !Number.isFinite(startAngle) || !Number.isFinite(endAngle)) {
      this.paintTrailSegment(x1, y1, x2, y2, radius, color, owner, stamp, fitBack);
      return;
    }

    const totalLen = Math.hypot(x2 - x1, y2 - y1);
    if (totalLen <= 1e-6) {
      this.paintTrailSegment(x1, y1, x2, y2, radius, color, owner, stamp, fitBack);
      return;
    }

    const stepLen = totalLen / steps;
    let prevX = x1;
    let prevY = y1;
    const occRadius = Math.max(OCC_STEP * 1.2, radius * this.collisionRadiusFactor);

    for (let i = 1; i <= steps; i++) {
      let currX;
      let currY;
      if (i === steps) {
        currX = x2;
        currY = y2;
      } else {
        const t = i / steps;
        const a = lerpAngle(startAngle, endAngle, t);
        currX = prevX + Math.cos(a) * stepLen;
        currY = prevY + Math.sin(a) * stepLen;
      }

      if (i === 1 && fitBack > 0) {
        const dx = currX - prevX;
        const dy = currY - prevY;
        const len = Math.hypot(dx, dy);
        if (len > 1e-6) {
          prevX -= (dx / len) * fitBack;
          prevY -= (dy / len) * fitBack;
        }
      }

      const ownerId = trailOpOwnerId(owner);
      this.pushOp({
        k: 's',
        x1: prevX,
        y1: prevY,
        x2: currX,
        y2: currY,
        r: radius,
        c: color,
        ...(ownerId !== null ? { o: ownerId } : {})
      });
      this.fillOccSegmentButt(prevX, prevY, currX, currY, occRadius, owner, stamp);
      prevX = currX;
      prevY = currY;
    }
  }

  paintTrailCap(x, y, radius, color, owner = 0, stamp = 0) {
    const ownerId = trailOpOwnerId(owner);
    this.pushOp({ k: 'p', x, y, r: radius, c: color, ...(ownerId !== null ? { o: ownerId } : {}) });
    this.fillOccDisc(x, y, radius, owner, stamp);
  }

  occupied(x, y) {
    const xi = Math.round(x * FIELD_RES);
    const yi = Math.round(y * FIELD_RES);
    if (xi < 0 || yi < 0 || xi >= this.occW || yi >= this.occH) return true;
    return this.occ[yi * this.occW + xi] === 1;
  }

  rebuildOccBlocks() {
    const occBW = this.occBW, occH = this.occH, occW = this.occW;
    this.occBlocks.fill(0);
    for (let yy = 0; yy < occH; yy += 1) {
      const rowBase = yy * occW;
      const blockRowBase = (yy >> OCC_BLOCK_SHIFT) * occBW;
      for (let xx = 0; xx < occW; xx += 1) {
        if (this.occ[rowBase + xx] === 1) {
          this.occBlocks[blockRowBase + (xx >> OCC_BLOCK_SHIFT)] += 1;
        }
      }
    }
  }

  occupiedDiscWithGrace(x, y, radius, selfOwner = 0, selfGraceTicks = 0, radiusInset = 0) {
    const occBW = this.occBW, occH = this.occH, occW = this.occW;
    const effRadius = Math.max(OCC_STEP * 0.6, radius - radiusInset);
    const radiusSq = effRadius * effRadius;
    const minX = Math.floor((x - effRadius) * FIELD_RES);
    const maxX = Math.ceil((x + effRadius) * FIELD_RES);
    const minY = Math.floor((y - effRadius) * FIELD_RES);
    const maxY = Math.ceil((y + effRadius) * FIELD_RES);

    if (minX < 0 || minY < 0 || maxX >= occW || maxY >= occH) return true;

    // Occupied cells can only exist inside blocks with a nonzero count, so an
    // all-empty block cover proves the fine scan below would find nothing.
    // The grace branch only ever skips occupied cells, never adds hits.
    // Same hoisting as the segment variant below: these are read-only for the
    // call, so lifting them out of the cell loops is pure code motion.
    const occ = this.occ;
    const occBlocks = this.occBlocks;
    const occOwner = this.occOwner;
    const occTick = this.occTick;
    const nowTick = this.tickCounter;
    const gracedOwner = selfOwner !== 0;

    let blocksEmpty = true;
    const bMaxY = maxY >> OCC_BLOCK_SHIFT;
    const bMaxX = maxX >> OCC_BLOCK_SHIFT;
    for (let by = minY >> OCC_BLOCK_SHIFT; by <= bMaxY && blocksEmpty; by += 1) {
      const blockRowBase = by * occBW;
      for (let bx = minX >> OCC_BLOCK_SHIFT; bx <= bMaxX; bx += 1) {
        if (occBlocks[blockRowBase + bx] !== 0) {
          blocksEmpty = false;
          break;
        }
      }
    }
    if (blocksEmpty) return false;

    for (let yy = minY; yy <= maxY; yy++) {
      const py = yy * OCC_STEP;
      const dy = py - y;
      const dySq = dy * dy;
      if (dySq > radiusSq) continue;
      const rowBase = yy * occW;

      for (let xx = minX; xx <= maxX; xx++) {
        const px = xx * OCC_STEP;
        const dx = px - x;
        if (dx * dx + dySq > radiusSq) continue;

        const idx = rowBase + xx;
        if (occ[idx] !== 1) continue;

        if (gracedOwner && occOwner[idx] === selfOwner) {
          const age = nowTick - occTick[idx];
          if (age >= 0 && age <= selfGraceTicks) continue;
        }

        return true;
      }
    }

    return false;
  }

  // Continuous-capsule variant of occupiedDiscWithGrace over a movement
  // segment: every grid cell within the effective radius of the segment is
  // checked exactly once. Discrete per-sample disc probes along the same
  // segment re-read ~90% of these cells per sample; deep bot rollouts call
  // this once per simulated tick instead. The capsule is the limit of dense
  // sampling, so it is never more permissive than any sampling stride.
  occupiedSegmentWithGrace(x1, y1, x2, y2, radius, selfOwner = 0, selfGraceTicks = 0, radiusInset = 0) {
    const occBW = this.occBW, occH = this.occH, occW = this.occW;
    const effRadius = Math.max(OCC_STEP * 0.6, radius - radiusInset);
    const radiusSq = effRadius * effRadius;
    const minX = Math.floor((Math.min(x1, x2) - effRadius) * FIELD_RES);
    const maxX = Math.ceil((Math.max(x1, x2) + effRadius) * FIELD_RES);
    const minY = Math.floor((Math.min(y1, y2) - effRadius) * FIELD_RES);
    const maxY = Math.ceil((Math.max(y1, y2) + effRadius) * FIELD_RES);

    if (minX < 0 || minY < 0 || maxX >= occW || maxY >= occH) return true;

    const segX = x2 - x1;
    const segY = y2 - y1;
    const segLenSq = segX * segX + segY * segY;

    // Hoist the grid views and the loop-invariant predicates. This is the
    // innermost loop of the whole bot — deep rollouts spend a quarter of all
    // CPU here — and reloading four typed arrays off `this` per cell costs
    // more than the arithmetic it guards. Values are read-only for the call,
    // so this is a pure code motion.
    const occ = this.occ;
    const occBlocks = this.occBlocks;
    const occOwner = this.occOwner;
    const occTick = this.occTick;
    const nowTick = this.tickCounter;
    const gracedOwner = selfOwner !== 0;
    const hasLength = segLenSq > 0;

    // Iterate block-summary chunks and skip empty ones individually: in the
    // half-dense webs where deep rollouts live, an all-or-nothing cover test
    // never short-circuits, but most 16-cell chunks are still empty.
    const bMinY = minY >> OCC_BLOCK_SHIFT;
    const bMaxY = maxY >> OCC_BLOCK_SHIFT;
    const bMinX = minX >> OCC_BLOCK_SHIFT;
    const bMaxX = maxX >> OCC_BLOCK_SHIFT;
    for (let by = bMinY; by <= bMaxY; by += 1) {
      const blockRowBase = by * occBW;
      const cellMinY = Math.max(minY, by << OCC_BLOCK_SHIFT);
      const cellMaxY = Math.min(maxY, ((by + 1) << OCC_BLOCK_SHIFT) - 1);
      for (let bx = bMinX; bx <= bMaxX; bx += 1) {
        if (occBlocks[blockRowBase + bx] === 0) continue;
        const cellMinX = Math.max(minX, bx << OCC_BLOCK_SHIFT);
        const cellMaxX = Math.min(maxX, ((bx + 1) << OCC_BLOCK_SHIFT) - 1);
        for (let yy = cellMinY; yy <= cellMaxY; yy++) {
          // OCC_STEP is exactly 1/FIELD_RES with FIELD_RES a power of two, so
          // scaling by it is bit-identical to the division it replaces and
          // keeps a divide out of the innermost occupancy loop.
          const py = yy * OCC_STEP;
          const rowBase = yy * occW;
          // Row-invariant half of the projection dot product. Precomputing the
          // exact same product and adding it in the same order keeps this
          // bit-identical, unlike folding the divide into a reciprocal.
          const rowDot = (py - y1) * segY;
          for (let xx = cellMinX; xx <= cellMaxX; xx++) {
            const idx = rowBase + xx;
            if (occ[idx] !== 1) continue;

            const px = xx * OCC_STEP;
            let t = 0;
            if (hasLength) {
              t = ((px - x1) * segX + rowDot) / segLenSq;
              if (t < 0) t = 0;
              else if (t > 1) t = 1;
            }
            const dx = px - (x1 + segX * t);
            const dy = py - (y1 + segY * t);
            if (dx * dx + dy * dy > radiusSq) continue;

            if (gracedOwner && occOwner[idx] === selfOwner) {
              const age = nowTick - occTick[idx];
              if (age >= 0 && age <= selfGraceTicks) continue;
            }

            return true;
          }
        }
      }
    }

    return false;
  }

  collidesSweptDisc(x1, y1, x2, y2, radius, selfOwner = 0) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    const selfGraceTicks = COLLISION_SELF_GRACE_TICKS;
    const radiusInset = this.collisionOccRadiusInset;

    if (len === 0) {
      return this.occupiedDiscWithGrace(x2, y2, radius, selfOwner, selfGraceTicks, radiusInset);
    }

    const startSkip = Math.min(radius * 0.3, len * 0.25);
    const startT = startSkip / len;
    const remainLen = Math.max(0, len - startSkip);
    const sampleStep = Math.max(OCC_STEP * 0.75, radius * 0.18);
    const checks = Math.max(2, Math.ceil(remainLen / sampleStep));

    for (let i = 0; i <= checks; i++) {
      const t = startT + (1 - startT) * (i / checks);
      const cx = x1 + dx * t;
      const cy = y1 + dy * t;
      if (this.occupiedDiscWithGrace(cx, cy, radius, selfOwner, selfGraceTicks, radiusInset)) {
        return true;
      }
    }

    return this.occupiedDiscWithGrace(x2, y2, radius, selfOwner, selfGraceTicks, radiusInset);
  }

  // Death-only diagnostic: same cell scan as occupiedDiscWithGrace, but
  // reports which trail was hit ({ owner, ageTicks }) instead of true/false,
  // so deaths can be attributed to own vs fresh-opponent ("seal") trail.
  trailHitInfoAt(x, y, radius, selfOwner = 0, selfGraceTicks = 0, radiusInset = 0) {
    const occH = this.occH, occW = this.occW;
    const effRadius = Math.max(OCC_STEP * 0.6, radius - radiusInset);
    const radiusSq = effRadius * effRadius;
    const minX = Math.floor((x - effRadius) * FIELD_RES);
    const maxX = Math.ceil((x + effRadius) * FIELD_RES);
    const minY = Math.floor((y - effRadius) * FIELD_RES);
    const maxY = Math.ceil((y + effRadius) * FIELD_RES);

    if (minX < 0 || minY < 0 || maxX >= occW || maxY >= occH) return null;

    for (let yy = minY; yy <= maxY; yy++) {
      const py = yy * OCC_STEP;
      const dy = py - y;
      const dySq = dy * dy;
      if (dySq > radiusSq) continue;

      for (let xx = minX; xx <= maxX; xx++) {
        const px = xx * OCC_STEP;
        const dx = px - x;
        if (dx * dx + dySq > radiusSq) continue;

        const idx = yy * occW + xx;
        if (this.occ[idx] !== 1) continue;

        if (selfOwner !== 0 && this.occOwner[idx] === selfOwner) {
          const age = this.tickCounter - this.occTick[idx];
          if (age >= 0 && age <= selfGraceTicks) continue;
        }

        return {
          owner: this.occOwner[idx],
          ageTicks: this.tickCounter - this.occTick[idx]
        };
      }
    }

    return null;
  }

  trailHitInfo(x1, y1, x2, y2, radius, selfOwner = 0) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    const selfGraceTicks = COLLISION_SELF_GRACE_TICKS;
    const radiusInset = this.collisionOccRadiusInset;

    if (len === 0) {
      return this.trailHitInfoAt(x2, y2, radius, selfOwner, selfGraceTicks, radiusInset);
    }

    const startSkip = Math.min(radius * 0.3, len * 0.25);
    const startT = startSkip / len;
    const remainLen = Math.max(0, len - startSkip);
    const sampleStep = Math.max(OCC_STEP * 0.75, radius * 0.18);
    const checks = Math.max(2, Math.ceil(remainLen / sampleStep));

    for (let i = 0; i <= checks; i++) {
      const t = startT + (1 - startT) * (i / checks);
      const cx = x1 + dx * t;
      const cy = y1 + dy * t;
      const hit = this.trailHitInfoAt(cx, cy, radius, selfOwner, selfGraceTicks, radiusInset);
      if (hit) return hit;
    }

    return this.trailHitInfoAt(x2, y2, radius, selfOwner, selfGraceTicks, radiusInset);
  }

  isPlayerFlying(player) {
    return (Number(player?.flyTicks) || 0) > 0;
  }

  isRespawnPointSafe(x, y, playerId, safeRadius = RESPAWN_SAFE_RADIUS) {
    const radius = Math.max(this.trailSize / 2, Number(safeRadius) || RESPAWN_SAFE_RADIUS);
    if (x - radius < 0 || y - radius < 0 || x + radius > this.fieldWidth || y + radius > this.fieldHeight) {
      return false;
    }
    if (this.occupiedDiscWithGrace(x, y, radius, 0, 0, 0)) return false;

    const minPlayerDist = radius + this.trailSize * 2;
    for (const other of this.players) {
      if (!other || !other.alive || other.id === playerId) continue;
      if (Math.hypot(other.x - x, other.y - y) < minPlayerDist) return false;
    }

    return true;
  }

  isRespawnHeadingSafe(x, y, angle, lookaheadDistance = this.bikeSpeed * 0.75) {
    const radius = this.trailSize / 2;
    const lookahead = Math.max(RESPAWN_SAFE_RADIUS, Number(lookaheadDistance) || 0);
    const headingX = Math.cos(angle);
    const headingY = Math.sin(angle);
    const wallAvoidDistance = Math.max(RESPAWN_SAFE_RADIUS * 2, this.trailSize * 14);
    const wallwardEpsilon = 1e-6;

    if (
      (x < wallAvoidDistance && headingX < -wallwardEpsilon)
      || (this.fieldWidth - x < wallAvoidDistance && headingX > wallwardEpsilon)
      || (y < wallAvoidDistance && headingY < -wallwardEpsilon)
      || (this.fieldHeight - y < wallAvoidDistance && headingY > wallwardEpsilon)
    ) {
      return false;
    }

    const endX = x + headingX * lookahead;
    const endY = y + headingY * lookahead;
    return (
      endX - radius >= 0
      && endY - radius >= 0
      && endX + radius <= this.fieldWidth
      && endY + radius <= this.fieldHeight
    );
  }

  chooseRespawnAngle(x, y) {
    const avoidDistance = Math.max(RESPAWN_SAFE_RADIUS * 2, this.trailSize * 14);
    let awayX = 0;
    let awayY = 0;
    const left = x;
    const right = this.fieldWidth - x;
    const top = y;
    const bottom = this.fieldHeight - y;

    if (left < avoidDistance) awayX += (avoidDistance - left) / avoidDistance;
    if (right < avoidDistance) awayX -= (avoidDistance - right) / avoidDistance;
    if (top < avoidDistance) awayY += (avoidDistance - top) / avoidDistance;
    if (bottom < avoidDistance) awayY -= (avoidDistance - bottom) / avoidDistance;

    const awayLen = Math.hypot(awayX, awayY);
    const centerAngle = Math.atan2((this.fieldHeight / 2) - y, (this.fieldWidth / 2) - x);
    const preferredAngle = awayLen > 1e-6 ? Math.atan2(awayY, awayX) : centerAngle;
    const jitter = awayLen > 1e-6 ? Math.PI * 0.42 : Math.PI;

    for (let i = 0; i < 24; i += 1) {
      const angle = preferredAngle + this.randomRange(-jitter, jitter);
      if (this.isRespawnHeadingSafe(x, y, angle)) return angle;
    }

    for (let i = 0; i < 24; i += 1) {
      const angle = this.randomRange(0, Math.PI * 2);
      if (this.isRespawnHeadingSafe(x, y, angle)) return angle;
    }

    return centerAngle;
  }

  findRespawnPoint(playerId) {
    const radius = Math.max(RESPAWN_SAFE_RADIUS, this.trailSize * 6);
    for (let i = 0; i < 80; i += 1) {
      const x = this.randomRange(radius, this.fieldWidth - radius);
      const y = this.randomRange(radius, this.fieldHeight - radius);
      if (!this.isRespawnPointSafe(x, y, playerId, radius)) continue;
      return {
        x,
        y,
        angle: this.chooseRespawnAngle(x, y)
      };
    }

    const centerX = this.fieldWidth / 2;
    const centerY = this.fieldHeight / 2;
    const x = clamp(centerX, radius, this.fieldWidth - radius);
    const y = clamp(centerY, radius, this.fieldHeight - radius);
    return {
      x,
      y,
      angle: this.chooseRespawnAngle(x, y)
    };
  }

  scheduleRespawn(player, now = this.lastTickMs) {
    if (!player) return;
    player.alive = false;
    player.respawnAtTick = this.tickCounter + RESPAWN_DELAY_TICKS;
    player.respawnAtMs = now + RESPAWN_DELAY_MS;
    player.effect = { type: null, ticks: 0, stacks: 0 };
    player.flyTicks = 0;
    player.graceTicks = 0;
    player.botTurnStreak = 0;
    player.holeDistanceLeft = 0;
    player.pendingHoleTicks = [];
    player.lastEmptyTick = this.tickCounter;
    this.clearPlayerInput(player.id);
  }

  respawnPlayer(player, now = this.lastTickMs) {
    if (!player || player.alive) return;
    const spawn = this.findRespawnPoint(player.id);
    player.x = spawn.x;
    player.y = spawn.y;
    player.angle = spawn.angle;
    player.alive = true;
    player.botTurnStreak = 0;
    player.graceTicks = Math.max(SPAWN_GRACE_TICKS, FLY_GRACE_TICKS);
    player.flyTicks = FLY_GRACE_TICKS;
    player.holeDistanceLeft = this.respawnTrailGapDistance;
    player.pendingHoleTicks = [];
    player.lastEmptyTick = this.tickCounter;
    player.effect = { type: null, ticks: 0, stacks: 0 };
    player.respawnAtTick = 0;
    player.respawnAtMs = 0;
    player.spawnAtMs = now;
    player.spawnDelayMs = 0;
    player.spawnScaleStart = Math.max(1.8, HEAD_SPAWN_START_SCALE_BASE * 0.7);
    player.spawnMarkerUntilMs = now + RESPAWN_MARKER_MS;
  }

  processRespawns(now = this.lastTickMs) {
    if (this.gameOver || this.waitingNext) return;
    for (const p of this.players) {
      if (!p || p.alive) continue;
      const respawnTick = Number(p.respawnAtTick) || 0;
      if (respawnTick <= 0 || this.tickCounter < respawnTick) continue;
      this.respawnPlayer(p, now);
    }
  }

  createPlayers(now = Date.now()) {
    const total = this.humanCount + this.botCount;
    if (this.scores.length !== total) {
      this.scores = Array.from({ length: total }, () => 0);
    }
    this.roundScores = Array.from({ length: total }, () => 0);

    const centerX = this.fieldWidth / 2;
    const centerY = this.fieldHeight / 2;
    // Random spawns each round: scatter start points across the field within a
    // wall margin, keeping a minimum separation so no two curves start on top of
    // each other. Rejection-sample a bounded number of tries and keep the most
    // spread-out candidate, so even a crowded field resolves deterministically.
    const marginX = this.fieldWidth * 0.13;
    const marginY = this.fieldHeight * 0.13;
    const minSeparation = Math.min(this.fieldWidth, this.fieldHeight) * 0.2;
    const spawnPoints = [];
    for (let i = 0; i < total; i += 1) {
      let best = null;
      let bestNearest = -Infinity;
      for (let attempt = 0; attempt < 30; attempt += 1) {
        const cx = this.randomRange(marginX, this.fieldWidth - marginX);
        const cy = this.randomRange(marginY, this.fieldHeight - marginY);
        let nearest = Infinity;
        for (const pt of spawnPoints) {
          const d = Math.hypot(pt.x - cx, pt.y - cy);
          if (d < nearest) nearest = d;
        }
        if (nearest > bestNearest) {
          bestNearest = nearest;
          best = { x: cx, y: cy };
        }
        if (nearest >= minSeparation) break;
      }
      spawnPoints.push(best);
    }

    this.players = Array.from({ length: total }, (_, i) => {
      const spawn = spawnPoints[i];
      // Head roughly toward the field center (+ jitter) so a spawn near an edge
      // drives into open space — spawn grace shields trails and heads, but never
      // walls, so a curve pointed at a nearby wall would still die.
      const angleToCenter = Math.atan2(centerY - spawn.y, centerX - spawn.x);

      const isBot = i >= this.humanCount;
      const label = isBot
        ? `BOT ${i - this.humanCount + 1}`
        : (this.humanCount === 1 && i === 0 ? 'YOU' : `P${i + 1}`);

      return {
        id: i,
        label,
        color: this.playerColors[i] || COLORS[i % COLORS.length],
        team: this.playerTeams[i] ?? null,
        mesh: this.playerMeshes[i]?.length > 1 ? this.playerMeshes[i] : null,
        isBot,
        botLastDir: 0,
        botTurnStreak: 0,
        x: spawn.x,
        y: spawn.y,
        angle: angleToCenter + this.randomRange(-0.7, 0.7),
        alive: true,
        holeDistanceLeft: 0,
        pendingHoleTicks: [],
        lastEmptyTick: -1,
        flyTicks: 0,
        graceTicks: SPAWN_GRACE_TICKS,
        roundDistance: 0,
        effect: { type: null, ticks: 0, stacks: 0 },
        respawnAtTick: 0,
        respawnAtMs: 0,
        spawnAtMs: now,
        spawnDelayMs: (i + 1) * HEAD_SPAWN_STAGGER_MS,
        spawnScaleStart: Math.max(1, HEAD_SPAWN_START_SCALE_BASE - i * HEAD_SPAWN_START_SCALE_STEP),
        spawnMarkerUntilMs: now + HEAD_SPAWN_MARKER_MS
      };
    });
  }

  // Start paths reset the frozen clock reading; the finalise paths must NOT --
  // they run at the moment a stopped match ends, and would wipe the very value
  // the end screen is there to show.
  resetMatch(now = Date.now()) {
    this.gameId = createGameId(now);
    const total = this.humanCount + this.botCount;
    this.scores = Array.from({ length: total }, () => 0);
    this.roundScores = Array.from({ length: total }, () => 0);
    this.matchActivity = [];
    this.matchTickCount = 0;
    this.roundNumber = 0;

    this.tickCounter = 0;
    this.running = false;
    this.waitingNext = false;
    this.gameOver = false;
    this.manualPaused = false;

    this.roundWinnerId = -1;
    this.matchWinnerId = -1;
    this.matchWinnerTeam = null;
    this.matchTie = false;
    this.nextRoundAt = 0;
    this.matchRestartAt = 0;
    this.roundEndsAt = 0;
    this.timeLeftAtEndMs = 0;
    this.matchStandings = [];
    this.standingsVersion += 1;
    this.roundStartFreezeUntilMs = 0;

    this.playerInputs.clear();
    for (let i = 0; i < this.humanCount; i++) {
      this.playerInputs.set(i, { left: false, right: false });
    }

    this.deathEvents = [];
    this.deathSeq = 0;
    this.deathEventsVersion += 1;
    this.powerups = [];
    this.powerupsActiveCount = 0;
    this.powerupsDirty = false;
    this.bumpPowerupsVersion();
    this.powerupSpawnTick = 0;
    this.clearFieldRequested = false;

    this.beginRound(now);
  }

  beginRound(now = Date.now()) {
    this.roundNumber = Math.max(0, Math.floor(Number(this.roundNumber) || 0)) + 1;
    this.tickCounter = 0;
    this.roundWinnerId = -1;
    this.nextRoundAt = 0;
    this.waitingNext = false;
    this.gameOver = false;
    this.matchWinnerId = -1;
    this.matchWinnerTeam = null;
    this.matchTie = false;
    this.matchRestartAt = 0;
    this.roundEndsAt = 0;
    this.timeLeftAtEndMs = 0;
    this.matchStandings = [];
    this.standingsVersion += 1;
    this.roundStartFreezeUntilMs = 0;
    this.powerups = [];
    this.powerupsActiveCount = 0;
    this.powerupsDirty = false;
    this.bumpPowerupsVersion();
    this.powerupSpawnTick = 0;
    this.clearFieldRequested = false;

    this.clearField();
    this.createPlayers(now);
    this.roundStartFreezeUntilMs = now + this.getRoundStartFreezeDurationMs();
    this.roundEndsAt = this.roundStartFreezeUntilMs + GEM_ROUND_DURATION_MS;
    this.timeLeftAtEndMs = 0;
    this.powerupSpawnTick = this.nextPowerupSpawnTick(true);

    this.running = !this.manualPaused;
  }

  pushDeathEvent(u, p) {
    const entry = {
      seq: ++this.deathSeq,
      tick: this.tickCounter,
      playerId: p.id,
      player: p.id === 0 ? 'YOU' : `BOT ${p.id}`,
      cause: u.deathCause || 'unknown',
      score: this.scores[p.id] ?? 0,
      roundScore: this.roundScores[p.id] ?? 0,
      from: { x: u.ox, y: u.oy },
      to: { x: u.nx, y: u.ny },
      wallGap: u.wallGap,
      details: u.deathInfo || {}
    };

    this.deathEvents.unshift(entry);
    if (this.deathEvents.length > DEATH_DEBUG_LIMIT) {
      this.deathEvents.length = DEATH_DEBUG_LIMIT;
    }
    this.deathEventsVersion += 1;
  }

  computeForcedWinnerId() {
    let bestId = 0;
    let bestRound = -Infinity;
    let bestScore = -Infinity;

    for (const p of this.players) {
      const roundScore = this.roundScores[p.id] ?? 0;
      const matchScore = this.scores[p.id] ?? 0;
      if (
        roundScore > bestRound
        || (roundScore === bestRound && matchScore > bestScore)
        || (roundScore === bestRound && matchScore === bestScore && p.id < bestId)
      ) {
        bestId = p.id;
        bestRound = roundScore;
        bestScore = matchScore;
      }
    }

    return bestId;
  }

  getRoundWinnerId(alivePlayers = null) {
    const alive = alivePlayers || this.players.filter((p) => p.alive);
    if (alive.length === 1) return alive[0].id;
    return this.computeForcedWinnerId();
  }

  // A match is a tie when 2+ players share the highest match score (and someone
  // actually scored). computeForcedWinnerId still names one winner for id-based
  // logic, but the result is announced as a tie.
  computeMatchTie() {
    let max = -Infinity;
    for (const p of this.players) {
      const s = this.scores[p.id] ?? 0;
      if (s > max) max = s;
    }
    if (max <= 0) return false;
    let atMax = 0;
    for (const p of this.players) {
      if ((this.scores[p.id] ?? 0) === max) atMax += 1;
    }
    return atMax > 1;
  }

  // Points are per player and always have been; a team's score is the sum of
  // its members, computed rather than tracked so it cannot drift from the
  // scores the standings show.
  teamScores() {
    return sumTeamScores(this.scores, this.playerTeams);
  }

  // Which side won on points, or null for a draw. Only meaningful in team mode.
  computeWinningTeam() {
    if (!this.teamMode) return null;
    const totals = this.teamScores();
    if (totals.fire === totals.water) return null;
    return totals.fire > totals.water ? 'fire' : 'water';
  }

  computeStandings() {
    return this.players
      .map((p) => ({
        id: p.id,
        label: p.id === 0 ? 'YOU' : `BOT ${p.id}`,
        color: p.color,
        score: this.scores[p.id] ?? 0
      }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.id - b.id;
      });
  }

  finalizeTimedMatch(now = Date.now()) {
    const winnerId = this.computeForcedWinnerId();
    this.running = false;
    this.waitingNext = false;
    this.gameOver = true;
    this.roundWinnerId = winnerId;
    this.matchWinnerId = winnerId;
    // A team match is won by the side with more points, so the tie that matters
    // is between the SIDES -- two team-mates level on points is not a draw.
    this.matchWinnerTeam = this.computeWinningTeam();
    this.matchTie = this.teamMode ? this.matchWinnerTeam === null : this.computeMatchTie();
    this.nextRoundAt = 0;
    this.matchRestartAt = 0;
    this.roundEndsAt = 0;
    this.matchStandings = this.computeStandings();
    this.standingsVersion += 1;
    this.roundStartFreezeUntilMs = 0;
    this.powerupSpawnTick = 0;
    this.clearFieldRequested = false;
    this.lastTickMs = now;
  }

  finalizeRound(roundWinnerId, now = Date.now()) {
    const target = this.getMatchTarget(this.players.length);
    const winnerId = this.scores.findIndex((s) => s >= target);

    if (winnerId !== -1) {
      this.running = false;
      this.waitingNext = false;
      this.gameOver = true;
      this.roundWinnerId = roundWinnerId;
      this.matchWinnerId = winnerId;
      this.matchTie = this.computeMatchTie();
      this.nextRoundAt = 0;
      this.matchRestartAt = 0;
      this.roundEndsAt = 0;
      this.matchStandings = this.computeStandings();
      this.standingsVersion += 1;
      this.roundStartFreezeUntilMs = 0;
      this.powerupSpawnTick = 0;
      this.clearFieldRequested = false;
      return;
    }

    // Keep sim active so surviving snakes can keep moving during inter-round countdown.
    this.running = true;
    this.waitingNext = true;
    this.roundWinnerId = roundWinnerId;
    this.nextRoundAt = now + INTERROUND_DELAY_MS;
    this.roundEndsAt = 0;
    this.matchStandings = [];
    this.standingsVersion += 1;
    this.roundStartFreezeUntilMs = 0;
    this.powerupSpawnTick = 0;
    this.clearFieldRequested = false;
  }

  // Per-match activity evidence, indexed by engine player id. Recorded so a
  // finished match can be judged on whether the opposition was genuinely
  // contesting rather than idling while somebody farmed gems uncontested; the
  // judging itself lives in server/highscore-policy.js. Match-scoped rather
  // than stored on the player, because players are rebuilt every round.
  //
  // steerTicks only ever moves for human-driven slots: humanDir is forced to 0
  // for anything bot-driven (including a self-bot), so the policy must decide
  // bot slots from their roster flags and never from this counter.
  matchActivityFor(playerId) {
    const id = Number(playerId);
    if (!Number.isInteger(id) || id < 0) return null;
    if (!Array.isArray(this.matchActivity)) this.matchActivity = [];
    let entry = this.matchActivity[id];
    if (!entry) {
      entry = { aliveTicks: 0, steerTicks: 0, gems: 0 };
      this.matchActivity[id] = entry;
    }
    return entry;
  }

  buildTickPlans() {
    const tickPlans = new Map();
    const fullTickSeconds = TICK_MS / 1000;
    this.matchTickCount = (Number(this.matchTickCount) || 0) + 1;

    for (const p of this.players) {
      if (!p.alive) continue;

      const effect = this.effectState(p);
      const isFlying = this.isPlayerFlying(p);
      const humanDir = p.isBot ? 0 : this.getPlayerDir(p.id);
      if (!p.isBot && typeof this.botLogic.observeHuman === 'function') {
        this.botLogic.observeHuman(p, humanDir);
      }

      const activity = this.matchActivityFor(p.id);
      if (activity) {
        activity.aliveTicks += 1;
        if (humanDir !== 0) activity.steerTicks += 1;
      }

      const speedNow = this.bikeSpeed * effect.speedMul;
      const fullTickDist = speedNow * fullTickSeconds;
      const pendingHoleTicks = Array.isArray(p.pendingHoleTicks) ? p.pendingHoleTicks : [];
      let writeIndex = 0;
      for (let i = 0; i < pendingHoleTicks.length; i += 1) {
        const holeTick = Number(pendingHoleTicks[i]);
        if (!Number.isFinite(holeTick)) continue;
        if (holeTick > this.tickCounter) {
          pendingHoleTicks[writeIndex] = holeTick;
          writeIndex += 1;
          continue;
        }
        if (p.holeDistanceLeft <= 0 && p.lastEmptyTick < this.tickCounter - 1) {
          p.holeDistanceLeft = this.holeSize;
        }
      }
      pendingHoleTicks.length = writeIndex;
      p.pendingHoleTicks = pendingHoleTicks;

      if (!isFlying && p.holeDistanceLeft <= 0 && this.randomChance(this.gapStartChancePerTick)) {
        pendingHoleTicks.push(this.tickCounter + HOLE_START_DELAY_TICKS);
      }

      let stepMode = 'WRITE';
      if (isFlying || p.holeDistanceLeft > 0) {
        p.holeDistanceLeft = Math.max(0, p.holeDistanceLeft - fullTickDist);
        stepMode = 'MOVE';
        p.lastEmptyTick = this.tickCounter;
      }
      const trailFitBack = stepMode === 'WRITE' && p.lastEmptyTick !== this.tickCounter - 1 ? 1 : 0;

      tickPlans.set(p.id, {
        speedNow,
        turnRadiusNow: this.turnRadiusForPlayer(p.id) * effect.turnRadiusMul,
        radius: this.trailSize / 2,
        stepMode,
        trailFitBack,
        isFlying,
        graceTicksAtStart: p.graceTicks,
        flyTicksAtStart: p.flyTicks,
        botDir: p.isBot ? this.botLogic.getDirBot(p) : 0
      });
    }

    return tickPlans;
  }

  simulateTickFrame(frameMs, tickPlans) {
    if (!(frameMs > TICK_FRAME_EPSILON_MS)) return;

    const updates = this._updates;
    updates.length = 0;

    for (const p of this.players) {
      if (!p.alive) continue;
      const plan = tickPlans.get(p.id);
      if (!plan) continue;

      const dir = p.isBot ? plan.botDir : this.getPlayerDir(p.id);
      if (p.isBot) {
        const prevBotDir = Math.sign(Number(p.botLastDir) || 0);
        p.botTurnStreak = dir !== 0 && dir === prevBotDir
          ? Math.max(0, Number(p.botTurnStreak) || 0) + 1
          : (dir !== 0 ? 1 : 0);
        p.botLastDir = dir;
      }
      const dist = plan.speedNow * (frameMs / 1000);
      const turnStep = (plan.speedNow / plan.turnRadiusNow) * (frameMs / 1000);
      const radius = plan.radius;
      const oa = p.angle;
      const na = oa + dir * turnStep;
      let nx = p.x + Math.cos(na) * dist;
      let ny = p.y + Math.sin(na) * dist;
      const stepMode = plan.stepMode;
      let drawStartT = 0;
      if (stepMode === 'MOVE') {
        drawStartT = 1;
      }

      let dead = false;
      let deathCause = null;
      let deathInfo = null;

      const wallGapBefore = Math.min(
        nx - radius,
        ny - radius,
        this.fieldWidth - radius - nx,
        this.fieldHeight - radius - ny
      );

      if (wallGapBefore < 0) {
        const minX = radius;
        const maxX = this.fieldWidth - radius;
        const minY = radius;
        const maxY = this.fieldHeight - radius;
        const moveDx = nx - p.x;
        const moveDy = ny - p.y;
        let hitT = 1;
        let hitAxis = 'none';

        const tryAxis = (candidate, axis) => {
          if (!Number.isFinite(candidate)) return;
          if (candidate < 0 || candidate > hitT) return;
          hitT = candidate;
          hitAxis = axis;
        };

        if (moveDx > 0) {
          tryAxis((maxX - p.x) / moveDx, 'x+');
        } else if (moveDx < 0) {
          tryAxis((minX - p.x) / moveDx, 'x-');
        }

        if (moveDy > 0) {
          tryAxis((maxY - p.y) / moveDy, 'y+');
        } else if (moveDy < 0) {
          tryAxis((minY - p.y) / moveDy, 'y-');
        }

        hitT = clamp(hitT, 0, 1);
        nx = p.x + moveDx * hitT;
        ny = p.y + moveDy * hitT;
        nx = clamp(nx, minX, maxX);
        ny = clamp(ny, minY, maxY);

        dead = true;
        deathCause = 'wall';
        deathInfo = { wallGapBefore, hitT, hitAxis };
      }

      if (!dead && p.graceTicks <= 0) {
        const trailHit = this.collidesSweptDisc(p.x, p.y, nx, ny, radius, p.id + 1);
        if (trailHit) {
          dead = true;
          deathCause = 'trail';
          deathInfo = this.trailHitInfo(p.x, p.y, nx, ny, radius, p.id + 1) || {};
        }
      }

      updates.push({
        p,
        ox: p.x,
        oy: p.y,
        nx,
        ny,
        oa,
        na,
        dead,
        radius,
        dist,
        stepMode,
        trailFitBack: plan.trailFitBack,
        drawStartT,
        deathCause,
        deathInfo,
        wallGap: Math.min(nx - radius, ny - radius, this.fieldWidth - radius - nx, this.fieldHeight - radius - ny)
      });
    }

    for (let i = 0; i < updates.length; i++) {
      for (let j = i + 1; j < updates.length; j++) {
        const a = updates[i];
        const b = updates[j];
        if (a.dead || b.dead) continue;
        if (a.p.graceTicks > 0 || b.p.graceTicks > 0) continue;

        const lim = (a.radius + b.radius) * this.collisionRadiusFactor;
        const limSq = lim * lim;
        const headSweepDistSq = segmentSegmentDistSq(a.ox, a.oy, a.nx, a.ny, b.ox, b.oy, b.nx, b.ny);
        if (headSweepDistSq > limSq) continue;

        a.dead = true;
        b.dead = true;
        a.deathCause = 'head_sweep';
        b.deathCause = 'head_sweep';
        a.deathInfo = { with: b.p.id, distSq: headSweepDistSq, limSq };
        b.deathInfo = { with: a.p.id, distSq: headSweepDistSq, limSq };
      }
    }

    for (const u of updates) {
      const p = u.p;
      p.x = u.nx;
      p.y = u.ny;
      p.angle = u.na;
      p.roundDistance += u.dist;

      if (u.dead) {
        this.pushDeathEvent(u, p);
        if (!(this.preserveBotTrailOnDeath && p.isBot)) {
          this.eraseTrailOwner(p.id);
        }
        this.scheduleRespawn(p, this.lastTickMs);
        continue;
      }

      const trailColor = this.trailColorFor(p);
      if (u.stepMode === 'WRITE') {
        this.paintTrailSegmentSubsteps(
          u.ox,
          u.oy,
          p.x,
          p.y,
          u.radius,
          trailColor,
          p.id + 1,
          this.tickCounter,
          u.trailFitBack,
          u.oa,
          u.na
        );
      } else if (u.stepMode === 'MOVE_WRITE') {
        const sx = u.ox + (u.nx - u.ox) * u.drawStartT;
        const sy = u.oy + (u.ny - u.oy) * u.drawStartT;
        const sa = lerpAngle(u.oa, u.na, u.drawStartT);
        this.paintTrailSegmentSubsteps(
          sx,
          sy,
          p.x,
          p.y,
          u.radius,
          trailColor,
          p.id + 1,
          this.tickCounter,
          0,
          sa,
          u.na
        );
      }
    }
  }

  tick(inputFrames = null) {
    this.tickCounter += 1;
    this.processRespawns(this.lastTickMs);

    if (this.powerupSpawnTick > 0 && this.tickCounter >= this.powerupSpawnTick) {
      const spawned = this.spawnPowerup();
      this.powerupSpawnTick = spawned
        ? this.nextPowerupSpawnTick(false)
        : this.tickCounter + POWERUP_SPAWN_RETRY_TICKS;
    }

    const frames = this.normalizeTickInputFrames(inputFrames);
    const tickPlans = this.buildTickPlans();
    for (const frame of frames) {
      this.applyHumanInputFrame(frame.playerInputs);
      this.simulateTickFrame(frame.dtMs, tickPlans);
    }
    for (const [playerId, plan] of tickPlans.entries()) {
      const player = this.players[playerId];
      if (!player) continue;
      if (plan.graceTicksAtStart > 0) {
        player.graceTicks = Math.max(0, plan.graceTicksAtStart - 1);
      }
      if (plan.flyTicksAtStart > 0) {
        player.flyTicks = Math.max(0, plan.flyTicksAtStart - 1);
      }
    }
    for (const p of this.players) {
      if (!p.alive) continue;
      this.processPowerupGrab(p);
    }

    if (this.clearFieldRequested) {
      this.clearFieldRequested = false;
      this.clearField();
    }

    this.tickEffects();
    this.compactPowerupsIfNeeded();

    if (this.roundEndsAt > 0 && this.lastTickMs >= this.roundEndsAt) {
      this.finalizeTimedMatch(this.lastTickMs);
    }
  }
}

export {
  CurveGameEngine,
  COLORS,
  COLOR_NAMES,
  MESH_COLORS,
  MESH_COLOR_OFFSET,
  PALETTE_SIZE,
  TRAIL_OPACITY_MIN_PERCENT,
  isMeshColorId,
  meshForColorId,
  meshStopsForColorId,
  paletteColorForId,
  paletteNameForId,
  DISCONNECTED_COLOR
};
